import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useSocket } from '../../context/socket';
import {
  LOCALOPS_OPS_LAST_TASK_ID_KEY,
  canViewLocalOpsOps,
} from '../../utils/localOpsOpsAccess';
import './localOpsOpsHealth.css';

const STATUS_LABELS = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  idle: 'Not checked',
};

const formatJgk = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0 JGK';
  return `${Math.round(amount).toLocaleString('en-US')} JGK`;
};

const formatUsd = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '--';
  return `$${amount.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return 'Not checked';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const truthyLabel = (value) => {
  if (value === true) return 'Pass';
  if (value === false) return 'Fail';
  return 'Unknown';
};

const TASK_STATUS_OPTIONS = [
  '',
  'created',
  'discoverable',
  'accepted',
  'in_progress',
  'completed',
  'cancelled',
  'expired',
];

const LAUNCH_ATTRIBUTION_ENDPOINT =
  'local-ops/debug/camden-launch-attribution';
const OPERATOR_VISIBILITY_ENDPOINT =
  'local-ops/debug/operator-visibility';
const CONCIERGE_INTAKE_ENDPOINT =
  'local-ops/intake-drafts/collision-specialist-ride/send-magic-link';

const COLLISION_SPECIALIST_PICKUP_ADDRESS =
  'Collision Specialist, 134 West Main Street, Camden, TN 38320';

const buildDefaultConciergeIntakeForm = () => ({
  deliveryAddress: '',
  customerEmail: '',
  customerPhone: '',
  notes: '',
});

const CAMDEN_LAUNCH_ATTRIBUTION_PARAMS = Object.freeze({
  regionSlug: 'camden',
  marketSlug: 'camden',
});

export const OPS_HEALTH_SNAPSHOT_VERSION = 1;
export const OPS_HEALTH_SNAPSHOT_PREFIX =
  'localopsOpsHealth:lastSnapshot:v1';
const OPS_HEALTH_MAX_SNAPSHOT_ROWS = 80;

const LAUNCH_EVENT_LABELS = Object.freeze({
  localops_task_published: 'Task posts',
  localops_task_accepted: 'Task accepts',
  localops_task_completion_verified: 'Task completions',
  localops_action_credit_awarded: 'Action Credit',
  localops_offering_published: 'Offering posts',
  localops_offering_reserved: 'Offering reservations',
  localops_offering_confirmation_requested: 'Confirmation requested',
  localops_offering_settlement_resolved: 'Offering settlements',
});

const LAUNCH_EVENT_ORDER = [
  'localops_task_published',
  'localops_task_accepted',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
  'localops_offering_published',
  'localops_offering_reserved',
  'localops_offering_confirmation_requested',
  'localops_offering_settlement_resolved',
];

const MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER = [
  'localops_task_published',
  'localops_task_accepted',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
];

const VISIT_EVENT_LABELS = Object.freeze({
  localops_region_landing_viewed: 'Landing views',
  localops_region_engaged_10s: '10s engaged',
  localops_region_engaged_60s: '60s engaged',
  localops_region_cta_clicked: 'CTA clicks',
});

const VISIT_EVENT_ORDER = [
  'localops_region_landing_viewed',
  'localops_region_engaged_10s',
  'localops_region_engaged_60s',
  'localops_region_cta_clicked',
];

const AUTH_EVENT_LABELS = Object.freeze({
  localops_magic_link_requested: 'Magic links requested',
  localops_magic_link_completed: 'Magic links completed',
  localops_magic_link_player_created: 'Magic-link players created',
  localops_magic_link_existing_player_login: 'Returning magic-link logins',
});

const AUTH_EVENT_ORDER = [
  'localops_magic_link_requested',
  'localops_magic_link_completed',
  'localops_magic_link_player_created',
  'localops_magic_link_existing_player_login',
];

const SHARE_FUNNEL_EVENT_LABELS = Object.freeze({
  localops_share_landing_viewed: 'Share landings',
  localops_share_accept_clicked: 'Landing Accept clicks',
  localops_share_continue_started: 'Continue started',
  localops_share_magic_link_requested: 'Magic links requested',
  localops_share_magic_link_completed: 'Magic links completed',
  localops_share_playermap_arrived: 'PlayerMap arrivals',
  localops_share_task_sheet_opened: 'Task Sheet opens',
  localops_share_task_accept_clicked: 'Sheet Accept clicks',
  localops_share_task_accept_succeeded: 'Accept succeeded',
  localops_share_task_accept_failed: 'Accept failed',
  localops_share_offering_sheet_opened: 'Offering Sheet opens',
  localops_share_offering_reserve_clicked: 'Reserve clicks',
  localops_share_offering_reserve_succeeded: 'Reserve succeeded',
  localops_share_offering_reserve_failed: 'Reserve failed',
  localops_task_completion_verified: 'Completed / verified',
  localops_action_credit_awarded: 'Action Credit',
});

const SHARE_FUNNEL_EVENT_ORDER = [
  'localops_share_landing_viewed',
  'localops_share_accept_clicked',
  'localops_share_continue_started',
  'localops_share_magic_link_requested',
  'localops_share_magic_link_completed',
  'localops_share_playermap_arrived',
  'localops_share_task_sheet_opened',
  'localops_share_task_accept_clicked',
  'localops_share_task_accept_succeeded',
  'localops_share_task_accept_failed',
  'localops_share_offering_sheet_opened',
  'localops_share_offering_reserve_clicked',
  'localops_share_offering_reserve_succeeded',
  'localops_share_offering_reserve_failed',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
];

const padDatePart = (value) => String(value).padStart(2, '0');

const toDateInputValue = (date) =>
  [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join('-');

const toLocalDateBoundaryIso = (value, boundary) => {
  if (!value) return '';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(year, month - 1, day);
  if (boundary === 'end') {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

export function buildDefaultTaskFinderFilters(now = new Date()) {
  const to = new Date(now);
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    playerId: '',
    q: '',
    from: toDateInputValue(from),
    to: toDateInputValue(to),
    status: '',
    boosted: false,
    rewarded: false,
    anomalyOnly: false,
    sort: 'newest',
    limit: '25',
  };
}

const buildTaskFinderParams = (filters) => {
  const params = {
    from: toLocalDateBoundaryIso(filters.from, 'start'),
    to: toLocalDateBoundaryIso(filters.to, 'end'),
    sort: filters.sort || 'newest',
    limit: filters.limit || '25',
  };
  if (filters.playerId) params.playerId = filters.playerId.trim();
  if (filters.q) params.q = filters.q.trim();
  if (filters.status) params.status = filters.status;
  if (filters.boosted) params.boosted = 'true';
  if (filters.rewarded) params.rewarded = 'true';
  if (filters.anomalyOnly) params.anomalyOnly = 'true';
  return params;
};

const formatCount = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count)) return '0';
  return Math.round(count).toLocaleString('en-US');
};

const getTotalLaunchEvents = (report) =>
  Object.values(report?.totalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalLaunchVisitEvents = (report) =>
  Object.values(report?.visitTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalLaunchAuthEvents = (report) =>
  Object.values(report?.authTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalMagicLinkPlayerActionEvents = (report) =>
  Object.values(report?.magicLinkPlayerActionTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalShareFunnelEvents = (report) =>
  Object.values(report?.shareFunnelTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const hasDurableSourceAttribution = (row) =>
  !!(
    row?.source ||
    row?.src ||
    row?.partnerSource ||
    row?.partnerAction ||
    row?.shareId
  );

const getSourceLabel = (row) => {
  if (!hasDurableSourceAttribution(row)) return 'Unattributed';
  return row?.sourceKey || row?.src || row?.source || 'Unattributed';
};

const formatSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.regionSlug) parts.push(`region ${row.regionSlug}`);
  if (row?.marketSlug) parts.push(`market ${row.marketSlug}`);
  if (row?.outcome) parts.push(`outcome ${row.outcome}`);
  return parts.join(' / ');
};

const buildLaunchSourceRows = (report) => {
  const bySource = new Map();
  Object.entries(report?.eventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = getSourceLabel(row);
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatVisitSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.ctaId) parts.push(`cta ${row.ctaId}`);
  return parts.join(' / ');
};

const buildVisitSourceRows = (report) => {
  if (Array.isArray(report?.visitSourceBreakdown)) {
    return report.visitSourceBreakdown
      .map((row) => ({
        sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
        details: formatVisitSourceDetails(row),
        counts: row?.counts || {},
        total: Number(row?.total || 0),
      }))
      .sort(
        (a, b) =>
          b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
      );
  }

  const bySource = new Map();
  Object.entries(report?.visitEventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = row?.sourceKey || row?.src || row?.source || 'direct';
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatVisitSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatAuthSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.returnToKind) parts.push(`return ${row.returnToKind}`);
  if (row?.playerState) parts.push(`player ${row.playerState}`);
  return parts.join(' / ');
};

const buildAuthSourceRows = (report) => {
  if (Array.isArray(report?.authSourceBreakdown)) {
    return report.authSourceBreakdown
      .map((row) => ({
        sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
        details: formatAuthSourceDetails(row),
        counts: row?.counts || {},
        total: Number(row?.total || 0),
      }))
      .sort(
        (a, b) =>
          b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
      );
  }

  const bySource = new Map();
  Object.entries(report?.authEventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = row?.sourceKey || row?.src || row?.source || 'direct';
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatAuthSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatMagicLinkPlayerActionDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.returnToKind) parts.push(`return ${row.returnToKind}`);
  if (row?.acquisitionEventName) {
    parts.push(`via ${AUTH_EVENT_LABELS[row.acquisitionEventName] || row.acquisitionEventName}`);
  }
  return parts.join(' / ');
};

const buildMagicLinkPlayerActionRows = (report) => {
  if (!Array.isArray(report?.magicLinkPlayerActionSourceBreakdown)) return [];
  return report.magicLinkPlayerActionSourceBreakdown
    .map((row) => ({
      sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
      details: formatMagicLinkPlayerActionDetails(row),
      counts: row?.counts || {},
      total: Number(row?.total || 0),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatShareFunnelDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.shareType) parts.push(`share ${row.shareType}`);
  if (row?.shareId) parts.push(`id ${row.shareId}`);
  if (row?.failureReason) parts.push(`failure ${row.failureReason}`);
  return parts.join(' / ');
};

const cleanDisplayText = (value, max = 140) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const getShareFunnelTaskKey = (row) =>
  cleanDisplayText(row?.taskId || row?.shareId || '', 120);

const getShareFunnelTaskTitle = (row, taskTitleLookup) => {
  const explicitTitle = cleanDisplayText(
    row?.taskTitle ||
      row?.offeringTitle ||
      row?.title ||
      row?.task?.title ||
      row?.offering?.title ||
      row?.entityTitle ||
      row?.shareTitle,
  );
  if (explicitTitle) return explicitTitle;
  const taskKey = getShareFunnelTaskKey(row);
  return taskKey ? taskTitleLookup.get(taskKey) || '' : '';
};

const buildShareFunnelDisplayLabel = (row, taskTitle) =>
  [
    row?.sourceKey || row?.src || row?.source || 'direct',
    row?.shareType || '',
    taskTitle || row?.taskId || row?.shareId || '',
    row?.failureReason || '',
  ]
    .filter(Boolean)
    .join(' / ');

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveConversionRate = (conversion, key, numerator, denominator) => {
  const explicit = Number(conversion?.[key]);
  if (Number.isFinite(explicit)) return explicit;
  const safeDenominator = toSafeNumber(denominator);
  if (safeDenominator <= 0) return null;
  return Number((toSafeNumber(numerator) / safeDenominator).toFixed(4));
};

const buildShareFunnelRows = (report, taskTitleLookup = new Map()) => {
  if (!Array.isArray(report?.shareFunnelSourceBreakdown)) return [];
  return report.shareFunnelSourceBreakdown
    .map((row) => {
      const counts = row?.counts || {};
      const conversion = row?.conversion || {};
      const taskTitle = getShareFunnelTaskTitle(row, taskTitleLookup);
      const sourceLabel = [
        row?.sourceKey || row?.src || row?.source || 'direct',
        row?.shareType || '',
        row?.taskId || row?.shareId || '',
        row?.failureReason || '',
      ]
        .filter(Boolean)
        .join(' / ');
      return {
        taskId: row?.taskId || row?.shareId || '',
        shareType: row?.shareType || '',
        sourceKey: row?.sourceKey || row?.src || row?.source || 'direct',
        sourceLabel,
        displayLabel: buildShareFunnelDisplayLabel(row, taskTitle),
        taskTitle,
        details: formatShareFunnelDetails(row),
        counts,
        total: Number(row?.total || 0),
        conversion: {
          ...conversion,
          landingToAcceptSuccessRate: resolveConversionRate(
            conversion,
            'landingToAcceptSuccessRate',
            counts.localops_share_task_accept_succeeded,
            counts.localops_share_landing_viewed,
          ),
          acceptSuccessToCompletionVerifiedRate: resolveConversionRate(
            conversion,
            'acceptSuccessToCompletionVerifiedRate',
            counts.localops_task_completion_verified,
            counts.localops_share_task_accept_succeeded,
          ),
          landingToCompletionVerifiedRate: resolveConversionRate(
            conversion,
            'landingToCompletionVerifiedRate',
            counts.localops_task_completion_verified,
            counts.localops_share_landing_viewed,
          ),
        },
      };
    })
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const addTaskTitleLookupEntry = (lookup, task) => {
  if (!task || typeof task !== 'object') return;
  const title = cleanDisplayText(
    task.title ||
      task.taskTitle ||
      task.offeringTitle ||
      task.name ||
      task.label,
  );
  if (!title) return;
  [
    task.taskId,
    task.offeringId,
    task.id,
    task._id,
    task.shareId,
    task.entityId,
  ].forEach((id) => {
    const key = cleanDisplayText(id, 120);
    if (key && !lookup.has(key)) lookup.set(key, title);
  });
};

const buildTaskTitleLookup = ({
  launchAttribution,
  operatorOfferingRows,
  operatorTaskRows,
  taskResults,
  taskReport,
} = {}) => {
  const lookup = new Map();
  (operatorTaskRows || []).forEach((task) =>
    addTaskTitleLookupEntry(lookup, task),
  );
  (operatorOfferingRows || []).forEach((offering) =>
    addTaskTitleLookupEntry(lookup, offering),
  );
  (taskResults || []).forEach((task) => addTaskTitleLookupEntry(lookup, task));
  addTaskTitleLookupEntry(lookup, taskReport?.task || taskReport);
  (launchAttribution?.shareFunnelSourceBreakdown || []).forEach((row) => {
    const title = cleanDisplayText(
      row?.taskTitle ||
        row?.offeringTitle ||
        row?.title ||
        row?.task?.title ||
        row?.offering?.title ||
        row?.entityTitle ||
        row?.shareTitle,
    );
    if (!title) return;
    [row?.taskId, row?.shareId].forEach((id) => {
      const key = cleanDisplayText(id, 120);
      if (key && !lookup.has(key)) lookup.set(key, title);
    });
  });
  return lookup;
};

const LAUNCH_DELTA_GROUPS = [
  {
    key: 'visitTotalsByEvent',
    label: 'Visit funnel',
    labels: VISIT_EVENT_LABELS,
  },
  {
    key: 'authTotalsByEvent',
    label: 'Magic-link auth',
    labels: AUTH_EVENT_LABELS,
  },
  {
    key: 'magicLinkPlayerActionTotalsByEvent',
    label: 'Magic-link player actions',
    labels: LAUNCH_EVENT_LABELS,
  },
  {
    key: 'shareFunnelTotalsByEvent',
    label: 'Share funnel',
    labels: SHARE_FUNNEL_EVENT_LABELS,
  },
  {
    key: 'totalsByEvent',
    label: 'Canonical task-source events',
    labels: LAUNCH_EVENT_LABELS,
  },
];

const LAUNCH_SOURCE_DELTA_GROUPS = [
  {
    key: 'visitSourceBreakdown',
    label: 'Visit source',
    labels: VISIT_EVENT_LABELS,
    buildRows: buildVisitSourceRows,
  },
  {
    key: 'authSourceBreakdown',
    label: 'Magic-link auth source',
    labels: AUTH_EVENT_LABELS,
    buildRows: buildAuthSourceRows,
  },
  {
    key: 'magicLinkPlayerActionSourceBreakdown',
    label: 'Magic-link player-action source',
    labels: LAUNCH_EVENT_LABELS,
    buildRows: buildMagicLinkPlayerActionRows,
  },
  {
    key: 'shareFunnelSourceBreakdown',
    label: 'Share source',
    labels: SHARE_FUNNEL_EVENT_LABELS,
    buildRows: buildShareFunnelRows,
  },
  {
    key: 'eventBreakdown',
    label: 'Canonical source',
    labels: LAUNCH_EVENT_LABELS,
    buildRows: buildLaunchSourceRows,
  },
];

const LAUNCH_SUMMARY_DELTA_FIELDS = [
  {
    path: ['magicLinkPlayerActionCoverage', 'attributed'],
    key: 'magicLinkPlayerActionCoverage:attributed',
    group: 'Magic-link player actions',
    label: 'Matched player actions',
  },
  {
    path: ['magicLinkPlayerActionCoverage', 'total'],
    key: 'magicLinkPlayerActionCoverage:total',
    group: 'Magic-link player actions',
    label: 'Total player actions',
  },
  {
    path: ['unattributedCanonicalEventCount'],
    key: 'unattributedCanonicalEventCount',
    group: 'Canonical task-source events',
    label: 'Unattributed canonical events',
  },
  {
    path: ['actionCreditJoinCoverage', 'attributed'],
    key: 'actionCreditJoinCoverage:attributed',
    group: 'Canonical task-source events',
    label: 'Source to Action Credit',
  },
];

const getPathNumber = (item, path) =>
  Number(path.reduce((current, part) => current?.[part], item) || 0);

const normalizeDeltaNumber = (value) => {
  if (value === null) return null;
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const addDelta = (deltas, item) => {
  const before = normalizeDeltaNumber(item.before);
  const after = normalizeDeltaNumber(item.after);
  if (before === null || after === null) return;
  const delta = after - before;
  if (!delta) return;
  deltas.push({
    ...item,
    before,
    after,
    delta,
  });
};

export const buildLaunchAttributionDeltas = (previous, next) => {
  if (!previous || !next) return [];
  const deltas = [];
  LAUNCH_DELTA_GROUPS.forEach((group) => {
    const previousCounts = previous?.[group.key] || {};
    const nextCounts = next?.[group.key] || {};
    const keys = new Set([
      ...Object.keys(previousCounts),
      ...Object.keys(nextCounts),
    ]);
    keys.forEach((eventName) => {
      addDelta(deltas, {
        key: `${group.key}:${eventName}`,
        type: 'metric',
        sectionKey: group.key,
        eventName,
        group: group.label,
        label: group.labels[eventName] || eventName,
        before: previousCounts[eventName],
        after: nextCounts[eventName],
      });
    });
  });

  LAUNCH_SOURCE_DELTA_GROUPS.forEach((group) => {
    const previousRows = new Map(
      group.buildRows(previous).map((row) => [row.sourceLabel, row]),
    );
    const nextRows = new Map(
      group.buildRows(next).map((row) => [row.sourceLabel, row]),
    );
    const sourceLabels = new Set([
      ...previousRows.keys(),
      ...nextRows.keys(),
    ]);
    sourceLabels.forEach((sourceLabel) => {
      const previousRow = previousRows.get(sourceLabel) || { counts: {} };
      const nextRow = nextRows.get(sourceLabel) || { counts: {} };
      const eventNames = new Set([
        ...Object.keys(previousRow.counts || {}),
        ...Object.keys(nextRow.counts || {}),
      ]);
      eventNames.forEach((eventName) => {
        addDelta(deltas, {
          key: `${group.key}:${sourceLabel}:${eventName}`,
          type: 'source-cell',
          sectionKey: group.key,
          sourceLabel,
          eventName,
          group: group.label,
          label: `${group.labels[eventName] || eventName} from ${sourceLabel}`,
          before: previousRow.counts?.[eventName],
          after: nextRow.counts?.[eventName],
        });
      });
      addDelta(deltas, {
        key: `${group.key}:${sourceLabel}:total`,
        type: 'source-row',
        sectionKey: group.key,
        sourceLabel,
        group: group.label,
        label: `Total from ${sourceLabel}`,
        before: previousRow.total,
        after: nextRow.total,
      });
    });
  });

  LAUNCH_SUMMARY_DELTA_FIELDS.forEach((field) => {
    addDelta(deltas, {
      key: field.key,
      type: 'summary',
      group: field.group,
      label: field.label,
      before: getPathNumber(previous, field.path),
      after: getPathNumber(next, field.path),
    });
  });

  return deltas.sort((a, b) => {
    const sectionCompare = String(a.group).localeCompare(String(b.group));
    if (sectionCompare) return sectionCompare;
    return Math.abs(b.delta) - Math.abs(a.delta);
  });
};

const buildDeltaLookup = (items = []) => {
  const byKey = new Map();
  items.forEach((item) => byKey.set(item.key, item));
  return byKey;
};

const LIVE_DELTA_FLASH_MS = 10000;

const markLiveDeltas = (items = []) =>
  items.map((item) => ({
    ...item,
    live: true,
  }));

const getMetricDelta = (lookup, sectionKey, eventName) =>
  lookup.get(`${sectionKey}:${eventName}`);

const getSourceCellDelta = (lookup, sectionKey, sourceLabel, eventName) =>
  lookup.get(`${sectionKey}:${sourceLabel}:${eventName}`);

const getSourceRowDelta = (lookup, sectionKey, sourceLabel) =>
  lookup.get(`${sectionKey}:${sourceLabel}:total`);

const getSummaryDelta = (lookup, key) => lookup.get(key);

const getMetricNumber = (value) => {
  if (value && typeof value === 'object' && value.status === 'notTracked') {
    return null;
  }
  if (value && typeof value === 'object' && 'value' in value) {
    return toSafeNumber(value.value);
  }
  return toSafeNumber(value);
};

const getPathMetricNumber = (item, path) =>
  getMetricNumber(path.reduce((current, part) => current?.[part], item));

const OPERATOR_SUMMARY_DELTA_FIELDS = [
  ['systemSummary', 'totalLocalPlayers'],
  ['systemSummary', 'newLocalPlayers7d'],
  ['systemSummary', 'reachablePlayers'],
  ['systemSummary', 'mapReadyPlayers'],
  ['systemSummary', 'taskEligiblePlayers'],
  ['systemSummary', 'verifiedLocalPlayers'],
  ['systemSummary', 'openFundedTasks'],
  ['systemSummary', 'acceptedTasks'],
  ['systemSummary', 'completedTasks'],
  ['systemSummary', 'availableOfferings'],
  ['systemSummary', 'actionCreditIssued'],
  ['systemSummary', 'sourceActivityRows'],
  ['activationFunnel', 'magicLinksRequested'],
  ['activationFunnel', 'magicLinksCompleted'],
  ['activationFunnel', 'playerMapArrivals'],
  ['activationFunnel', 'locationSaved'],
  ['activationFunnel', 'firstTaskCreated'],
  ['activationFunnel', 'firstOfferingCreated'],
  ['locationMapReadiness', 'saved'],
  ['locationMapReadiness', 'missing'],
  ['locationMapReadiness', 'stale'],
];

const OPERATOR_ROW_DELTA_GROUPS = [
  {
    rowsKey: 'activePlayerRows',
    idKey: 'playerId',
    keyPrefix: 'activePlayerRows',
    fields: [
      ['actionCreditCount'],
      ['magicLink', 'loginCount'],
      ['activationEmail', 'sendCount'],
    ],
  },
  {
    rowsKey: 'taskRows',
    idKey: 'taskId',
    keyPrefix: 'taskRows',
    fields: [
      ['reopenCount'],
      ['helperReleaseCount'],
      ['fanout', 'emailSentCount'],
      ['fanout', 'emailPixelOpens'],
      ['fanout', 'ownerReNotifyCount'],
      ['fanout', 'lastOwnerReNotifyRecipientCount'],
      ['fanout', 'lastOwnerReNotifyEmailSentCount'],
      ['raisePay', 'renotifyRecipientCount'],
      ['raisePay', 'renotifyEmailSentCount'],
    ],
  },
  {
    rowsKey: 'offeringRows',
    idKey: 'offeringId',
    keyPrefix: 'offeringRows',
    fields: [
      ['reservations'],
      ['fulfilledOrders'],
      ['shareLandings'],
    ],
  },
  {
    rowsKey: 'sourceAttributionRows',
    idKey: 'sourceKey',
    keyPrefix: 'sourceAttributionRows',
    fields: [['total']],
  },
  {
    rowsKey: 'emailNotificationProofRows',
    idKey: 'proofId',
    fallbackIdKey: 'notificationId',
    keyPrefix: 'emailNotificationProofRows',
    fields: [['pixelOpenCount']],
  },
];

const buildOperatorRowMap = (rows = [], idKey, fallbackIdKey) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = row?.[idKey] || (fallbackIdKey ? row?.[fallbackIdKey] : '');
    if (key) map.set(String(key), row);
  });
  return map;
};

export const buildOperatorVisibilityDeltas = (previous, next) => {
  if (!previous || !next) return [];
  const deltas = [];
  OPERATOR_SUMMARY_DELTA_FIELDS.forEach((path) => {
    addDelta(deltas, {
      key: `operator:${path.join('.')}`,
      group: 'LocalOps Operator Visibility',
      label: path.join('.'),
      before: getPathMetricNumber(previous, path),
      after: getPathMetricNumber(next, path),
    });
  });

  OPERATOR_ROW_DELTA_GROUPS.forEach((group) => {
    const previousRows = buildOperatorRowMap(
      previous?.[group.rowsKey],
      group.idKey,
      group.fallbackIdKey,
    );
    const nextRows = buildOperatorRowMap(
      next?.[group.rowsKey],
      group.idKey,
      group.fallbackIdKey,
    );
    const rowIds = new Set([...previousRows.keys(), ...nextRows.keys()]);
    rowIds.forEach((rowId) => {
      const previousRow = previousRows.get(rowId) || {};
      const nextRow = nextRows.get(rowId) || {};
      group.fields.forEach((fieldPath) => {
        addDelta(deltas, {
          key: `operator:${group.keyPrefix}:${rowId}:${fieldPath.join('.')}`,
          group: 'LocalOps Operator Visibility',
          label: fieldPath.join('.'),
          before: getPathMetricNumber(previousRow, fieldPath),
          after: getPathMetricNumber(nextRow, fieldPath),
        });
      });
    });
  });

  return deltas;
};

const formatDeltaValue = (delta) => {
  if (!delta) return '';
  if (delta.delta > 0) return `+${formatCount(delta.delta)}`;
  return 'Updated';
};

const shouldHighlightDelta = (delta) => !!delta;

const formatShortId = (value) => {
  const text = cleanDisplayText(value, 80);
  if (!text) return '';
  if (text.length <= 14) return text;
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
};

const EntityDebugId = ({ label, value }) => {
  const shortId = formatShortId(value);
  if (!shortId) return null;
  return (
    <small className="ops-health-entity-id">
      {label} {shortId}
    </small>
  );
};

const getPlayerDisplayLabel = (row) =>
  cleanDisplayText(
    row?.handle ||
      row?.playerId ||
      row?.player?.playerId ||
      row?.displayName ||
      'Unknown player',
  );

const getTaskDisplayTitle = (row) =>
  cleanDisplayText(
    row?.title ||
      row?.taskTitle ||
      row?.task?.title ||
      row?.entityTitle ||
      'Unknown task',
  );

const getOfferingDisplayTitle = (row) =>
  cleanDisplayText(
    row?.title ||
      row?.offeringTitle ||
      row?.offering?.title ||
      row?.entityTitle ||
      'Unknown offering',
  );

const formatActionCreditCoverage = (coverage) => {
  const total = Number(coverage?.total || 0);
  if (!total) return 'No Action Credit events';
  const attributed = Number(coverage?.attributed || 0);
  const rawCoverage = Number(coverage?.coverage);
  const percent = Number.isFinite(rawCoverage)
    ? `${(rawCoverage * 100).toFixed(1).replace(/\.0$/, '')}%`
    : 'Unknown';
  return `${formatCount(attributed)} of ${formatCount(total)} attributed (${percent})`;
};

const formatConversionPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '--';
  return `${(parsed * 100).toFixed(1).replace(/\.0$/, '')}%`;
};

const formatConversionWithFraction = (value, numerator, denominator) => {
  const safeNumerator = toSafeNumber(numerator);
  const safeDenominator = toSafeNumber(denominator);
  if (safeDenominator <= 0) return '--';
  return `${formatConversionPercent(value)} (${formatCount(safeNumerator)} / ${formatCount(safeDenominator)})`;
};

const addReadinessAnomalies = (items, readiness) => {
  const summary = readiness?.summary || {};
  (summary.failures || []).forEach((failure) => {
    items.push({
      source: 'readiness',
      severity: 'error',
      code: failure,
      message: failure,
    });
  });
  (summary.warnings || []).forEach((warning) => {
    items.push({
      source: 'readiness',
      severity: 'warning',
      code: warning,
      message: warning,
    });
  });
};

const addRollupAnomalies = (items, rollup) => {
  const duplicateKeys = rollup?.duplicateIdempotencyKeys || [];
  duplicateKeys.forEach((key) => {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'duplicate_idempotency_key',
      message: `Duplicate idempotency key: ${key}`,
    });
  });

  const missingRefs = Number(rollup?.missingSourceTransactionRefCount || 0);
  if (missingRefs > 0) {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'missing_source_transaction_ref',
      message: `${missingRefs} journal entries are missing source transaction references.`,
    });
  }
};

const addTaskAnomalies = (items, taskReport) => {
  (taskReport?.anomalies || []).forEach((item) => {
    items.push({
      source: 'task',
      severity: item.severity || 'warning',
      code: item.code || 'task_anomaly',
      message: item.message || item.code || 'Task accounting anomaly',
    });
  });
};

export function collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }) {
  const items = [];
  addReadinessAnomalies(items, readiness);
  addRollupAnomalies(items, rollup);
  addTaskAnomalies(items, taskReport);
  return items;
}

export function deriveLocalOpsOpsStatus({ readiness, rollup, anomalies, error }) {
  if (error) return 'red';
  if (!readiness || !rollup) return 'idle';
  if (readiness?.summary?.pass === false || rollup?.pass === false) return 'red';
  if ((anomalies || []).some((item) => item.severity === 'error')) return 'red';
  if ((anomalies || []).length > 0) return 'yellow';
  return 'green';
}

const DeltaBadge = ({ delta }) =>
  delta ? (
    <em
      className={`ops-health-delta-badge${
        delta.delta < 0 ? ' ops-health-delta-badge--neutral' : ''
      }${
        delta.live ? ' ops-health-delta-badge--live' : ''
      }`}
    >
      {formatDeltaValue(delta)}
    </em>
  ) : null;

const isTypedMetric = (value) =>
  value &&
  typeof value === 'object' &&
  Object.prototype.hasOwnProperty.call(value, 'status') &&
  Object.prototype.hasOwnProperty.call(value, 'label');

const getMetricValue = (value) => {
  if (isTypedMetric(value)) return value.label;
  if (value === undefined || value === null || value === '') return '--';
  return value;
};

const Metric = ({ label, value, delta }) => (
  <div
    className={`ops-health-metric${
      shouldHighlightDelta(delta) ? ' ops-health-metric--changed' : ''
    }${
      delta?.live ? ' ops-health-metric--live-changed' : ''
    }${
      isTypedMetric(value) && value.status === 'notTracked'
        ? ' ops-health-metric--not-tracked'
        : ''
    }`}
  >
    <span>{label}</span>
    <strong>{getMetricValue(value)}</strong>
    <DeltaBadge delta={delta} />
  </div>
);

const OperatorField = ({ label, value, delta }) => (
  <span
    className={`ops-health-operator-field${
      shouldHighlightDelta(delta) ? ' ops-health-operator-field--changed' : ''
    }${
      delta?.live ? ' ops-health-operator-field--live-changed' : ''
    }`}
  >
    <small>{label}</small>
    <strong>
      {getMetricValue(value)}
      <DeltaBadge delta={delta} />
    </strong>
  </span>
);

const OPEN_TASK_STATUSES = new Set([
  'created',
  'discoverable',
  'open',
  'published',
  'funded',
  'available',
]);

const ACCEPTED_TASK_STATUSES = new Set(['accepted', 'in_progress']);
const COMPLETED_TASK_STATUSES = new Set(['completed', 'verified', 'paid']);

const getTrackedMetricNumber = (value) => {
  if (isTypedMetric(value) && value.status === 'notTracked') return null;
  if (value === undefined || value === null || value === '') return null;
  const rawValue = isTypedMetric(value) ? value.value : value;
  if (rawValue === undefined || rawValue === null || rawValue === '') return null;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const cleanSnapshotText = (value, max = 120) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const stableSnapshotHash = (value) => {
  const text = cleanSnapshotText(value, 512);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const buildSnapshotFilterHash = (filters = {}) => {
  const normalized = Object.keys(filters || {})
    .sort()
    .reduce((acc, key) => {
      const value = cleanSnapshotText(filters[key], 80);
      if (value) acc[key] = value;
      return acc;
    }, {});
  return stableSnapshotHash(JSON.stringify(normalized));
};

export const buildOpsHealthSnapshotKey = ({
  reportType = 'unknown',
  filters = {},
} = {}) => {
  const normalizedReportType = cleanSnapshotText(reportType, 80) || 'unknown';
  const marketSlug =
    cleanSnapshotText(filters.marketSlug || filters.market, 80) || 'camden';
  const regionSlug =
    cleanSnapshotText(filters.regionSlug || filters.region || marketSlug, 80) ||
    'camden';
  return [
    OPS_HEALTH_SNAPSHOT_PREFIX,
    normalizedReportType,
    marketSlug,
    regionSlug,
    buildSnapshotFilterHash(filters),
  ].join(':');
};

export const getOpsSnapshotRowKey = (value) => {
  const cleaned = cleanSnapshotText(value, 240);
  return cleaned ? `h_${stableSnapshotHash(cleaned)}` : '';
};

const toSnapshotCount = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSnapshotMetric = (value) => {
  if (isTypedMetric(value)) {
    if (value.status === 'notTracked') {
      return {
        status: 'notTracked',
        label: cleanSnapshotText(value.label || 'not tracked', 80),
      };
    }
    const metricValue = getTrackedMetricNumber(value);
    return metricValue === null ? undefined : { value: metricValue };
  }
  const parsed = getTrackedMetricNumber(value);
  return parsed === null ? undefined : parsed;
};

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, item]) => {
    if (item !== undefined && item !== null && item !== '') acc[key] = item;
    return acc;
  }, {});

const pickMetricTree = (source = {}, fields = []) =>
  compactObject(
    fields.reduce((acc, field) => {
      const value = toSnapshotMetric(source?.[field]);
      if (value !== undefined) acc[field] = value;
      return acc;
    }, {}),
  );

const pickCounts = (counts = {}) =>
  Object.entries(counts || {}).reduce((acc, [eventName, value]) => {
    const safeEventName = cleanSnapshotText(eventName, 120);
    if (safeEventName) acc[safeEventName] = toSnapshotCount(value);
    return acc;
  }, {});

const pickConversion = (conversion = {}) =>
  Object.entries(conversion || {}).reduce((acc, [key, value]) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) acc[cleanSnapshotText(key, 120)] = parsed;
    return acc;
  }, {});

const limitSnapshotRows = (rows = []) =>
  Array.isArray(rows) ? rows.slice(0, OPS_HEALTH_MAX_SNAPSHOT_ROWS) : [];

const buildLaunchSourceSnapshotRows = (rows = [], options = {}) =>
  limitSnapshotRows(rows).map((row) => {
    const snapshotRow = {
      sourceKey: cleanSnapshotText(row?.sourceKey || row?.src || row?.source),
      source: cleanSnapshotText(row?.source),
      src: cleanSnapshotText(row?.src),
      routeKind: cleanSnapshotText(row?.routeKind),
      intent: cleanSnapshotText(row?.intent),
      returnToKind: cleanSnapshotText(row?.returnToKind),
      playerState: cleanSnapshotText(row?.playerState),
      acquisitionEventName: cleanSnapshotText(row?.acquisitionEventName),
      shareType: cleanSnapshotText(row?.shareType),
      shareId: options.keepShareKeys ? cleanSnapshotText(row?.shareId) : '',
      taskId: options.keepShareKeys ? cleanSnapshotText(row?.taskId) : '',
      failureReason: cleanSnapshotText(row?.failureReason),
      counts: pickCounts(row?.counts),
      total: toSnapshotCount(row?.total),
      conversion: pickConversion(row?.conversion),
    };
    if (row?.count !== undefined && row?.count !== null && row?.count !== '') {
      snapshotRow.count = toSnapshotCount(row.count);
    }
    return compactObject(snapshotRow);
  });

export const buildLaunchAttributionSnapshot = (report = {}) => ({
  visitTotalsByEvent: pickCounts(report?.visitTotalsByEvent),
  authTotalsByEvent: pickCounts(report?.authTotalsByEvent),
  magicLinkPlayerActionTotalsByEvent: pickCounts(
    report?.magicLinkPlayerActionTotalsByEvent,
  ),
  shareFunnelTotalsByEvent: pickCounts(report?.shareFunnelTotalsByEvent),
  totalsByEvent: pickCounts(report?.totalsByEvent),
  visitSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.visitSourceBreakdown,
  ),
  authSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.authSourceBreakdown,
  ),
  magicLinkPlayerActionSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.magicLinkPlayerActionSourceBreakdown,
  ),
  shareFunnelSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.shareFunnelSourceBreakdown,
    { keepShareKeys: true },
  ),
  eventBreakdown: Object.entries(report?.eventBreakdown || {}).reduce(
    (acc, [eventName, rows]) => {
      const safeEventName = cleanSnapshotText(eventName, 120);
      if (safeEventName) {
        acc[safeEventName] = buildLaunchSourceSnapshotRows(rows);
      }
      return acc;
    },
    {},
  ),
  magicLinkPlayerActionCoverage: pickMetricTree(
    report?.magicLinkPlayerActionCoverage,
    ['attributed', 'total', 'unmatched'],
  ),
  actionCreditJoinCoverage: pickMetricTree(report?.actionCreditJoinCoverage, [
    'attributed',
    'total',
    'unattributed',
  ]),
  unattributedCanonicalEventCount: toSnapshotCount(
    report?.unattributedCanonicalEventCount,
  ),
});

const buildOperatorRowSnapshot = (rows = [], idField, fields, nested = {}) =>
  limitSnapshotRows(rows)
    .map((row) => {
      const id = getOpsSnapshotRowKey(row?.[idField]);
      if (!id) return null;
      const snapshot = { [idField]: id };
      fields.forEach((field) => {
        const value = toSnapshotMetric(row?.[field]);
        if (value !== undefined) snapshot[field] = value;
      });
      Object.entries(nested).forEach(([nestedKey, nestedFields]) => {
        const nestedSnapshot = pickMetricTree(row?.[nestedKey], nestedFields);
        if (Object.keys(nestedSnapshot).length) {
          snapshot[nestedKey] = nestedSnapshot;
        }
      });
      return snapshot;
    })
    .filter(Boolean);

export const buildOperatorVisibilitySnapshot = (report = {}) => ({
  systemSummary: pickMetricTree(report?.systemSummary, [
    'totalLocalPlayers',
    'newLocalPlayers7d',
    'reachablePlayers',
    'mapReadyPlayers',
    'taskEligiblePlayers',
    'verifiedLocalPlayers',
    'openFundedTasks',
    'acceptedTasks',
    'completedTasks',
    'availableOfferings',
    'actionCreditIssued',
    'sourceActivityRows',
  ]),
  activationFunnel: pickMetricTree(report?.activationFunnel, [
    'magicLinksRequested',
    'magicLinksCompleted',
    'playerMapArrivals',
    'locationSaved',
    'firstTaskCreated',
    'firstOfferingCreated',
  ]),
  locationMapReadiness: pickMetricTree(report?.locationMapReadiness, [
    'saved',
    'missing',
    'stale',
  ]),
  activePlayerRows: buildOperatorRowSnapshot(
    report?.activePlayerRows,
    'playerId',
    ['actionCreditCount'],
    {
      magicLink: ['loginCount'],
      activationEmail: ['sendCount'],
    },
  ),
  taskRows: buildOperatorRowSnapshot(
    report?.taskRows,
    'taskId',
    ['reopenCount', 'helperReleaseCount'],
    {
      fanout: [
        'emailSentCount',
        'emailPixelOpens',
        'ownerReNotifyCount',
        'lastOwnerReNotifyRecipientCount',
        'lastOwnerReNotifyEmailSentCount',
      ],
      raisePay: [
        'renotifyRecipientCount',
        'renotifyEmailSentCount',
      ],
    },
  ),
  offeringRows: buildOperatorRowSnapshot(report?.offeringRows, 'offeringId', [
    'reservations',
    'fulfilledOrders',
    'shareLandings',
  ]),
  sourceAttributionRows: limitSnapshotRows(report?.sourceAttributionRows).map(
    (row) =>
      compactObject({
        sourceKey: cleanSnapshotText(row?.sourceKey || row?.src || row?.source),
        total: toSnapshotMetric(row?.total),
      }),
  ),
  emailNotificationProofRows: limitSnapshotRows(
    report?.emailNotificationProofRows,
  )
    .map((row) => {
      const proofKey = getOpsSnapshotRowKey(
        row?.proofId || row?.notificationId,
      );
      if (!proofKey) return null;
      return compactObject({
        proofId: proofKey,
        notificationId: proofKey,
        pixelOpenCount: toSnapshotMetric(row?.pixelOpenCount),
      });
    })
    .filter(Boolean),
});

export const readOpsHealthSnapshot = (key) => {
  if (typeof window === 'undefined' || !key) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '');
    if (parsed?.version !== OPS_HEALTH_SNAPSHOT_VERSION) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

export const writeOpsHealthSnapshot = (key, snapshot) => {
  if (typeof window === 'undefined' || !key || !snapshot) return false;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        version: OPS_HEALTH_SNAPSHOT_VERSION,
        capturedAt: new Date().toISOString(),
        snapshot,
      }),
    );
    return true;
  } catch (err) {
    return false;
  }
};

const getTaskStatus = (row) => String(row?.status || '').toLowerCase();

const isOpenTask = (row) => OPEN_TASK_STATUSES.has(getTaskStatus(row));

const isAcceptedTask = (row) =>
  ACCEPTED_TASK_STATUSES.has(getTaskStatus(row)) || !!row?.acceptedHelperPlayerId;

const getTaskId = (row) =>
  cleanDisplayText(row?.taskId || row?.id || row?._id || '', 120);

const hasActionCreditCompletionProof = (row, completedTaskIds = new Set()) => {
  const taskId = getTaskId(row);
  return !!taskId && completedTaskIds.has(taskId);
};

const isCompletedTask = (row, shareCounts, completedTaskIds) =>
  COMPLETED_TASK_STATUSES.has(getTaskStatus(row)) ||
  !!row?.completedAt ||
  !!row?.completedAtUtc ||
  !!row?.verifiedAt ||
  !!row?.verifiedAtUtc ||
  !!row?.completionVerifiedAt ||
  hasActionCreditCompletionProof(row, completedTaskIds) ||
  toSafeNumber(shareCounts?.localops_task_completion_verified) > 0 ||
  toSafeNumber(shareCounts?.localops_action_credit_awarded) > 0;

const hoursSince = (value, now = new Date()) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const nowDate = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(nowDate.getTime())) return null;
  return Math.max(0, Math.floor((nowDate.getTime() - date.getTime()) / 3600000));
};

const formatAgeHours = (hours) => {
  if (hours === null) return 'unknown age';
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const aggregateShareCounts = (rows = []) =>
  rows.reduce((totals, row) => {
    Object.entries(row?.counts || {}).forEach(([eventName, value]) => {
      totals[eventName] = Number(totals[eventName] || 0) + Number(value || 0);
    });
    return totals;
  }, {});

const buildShareCountsByTaskId = (shareFunnelRows = []) => {
  const byTaskId = new Map();
  shareFunnelRows.forEach((row) => {
    const taskId = cleanDisplayText(row?.taskId, 120);
    if (!taskId) return;
    if (!byTaskId.has(taskId)) byTaskId.set(taskId, []);
    byTaskId.get(taskId).push(row);
  });
  const countsByTaskId = new Map();
  byTaskId.forEach((rows, taskId) => {
    countsByTaskId.set(taskId, aggregateShareCounts(rows));
  });
  return countsByTaskId;
};

const getShareCount = (shareCounts, eventName) =>
  shareCounts ? toSafeNumber(shareCounts[eventName]) : null;

const buildTaskEvidence = ({
  fanoutSent,
  emailOpened,
  shareCounts,
  helper,
  acceptedAge,
}) => {
  const parts = [];
  if (fanoutSent !== null) parts.push(`fanout sent: ${formatCount(fanoutSent)}`);
  if (emailOpened !== null) parts.push(`email opened: ${formatCount(emailOpened)}`);
  if (shareCounts) {
    parts.push(
      `share landings: ${formatCount(getShareCount(shareCounts, 'localops_share_landing_viewed'))}`,
    );
    parts.push(
      `accept success: ${formatCount(getShareCount(shareCounts, 'localops_share_task_accept_succeeded'))}`,
    );
  }
  if (acceptedAge !== null) parts.push(`accepted age: ${formatAgeHours(acceptedAge)}`);
  if (helper) parts.push(`helper: ${helper}`);
  return parts.length ? parts.join(' / ') : 'Need more data';
};

const deriveTaskActionability = (row, shareCounts, now, completedTaskIds) => {
  const fanoutSent = getTrackedMetricNumber(row?.fanout?.emailSentCount);
  const emailOpened = getTrackedMetricNumber(row?.fanout?.emailPixelOpens);
  const helper = row?.acceptedHelperPlayerId || 'unassigned';
  const actionCreditCompletion = hasActionCreditCompletionProof(
    row,
    completedTaskIds,
  );
  const acceptedAge = isAcceptedTask(row)
    ? hoursSince(row?.acceptedAt || row?.acceptedAtUtc || row?.updatedAt, now)
    : null;
  const shareLandings = getShareCount(shareCounts, 'localops_share_landing_viewed');
  const landingAcceptClicks = getShareCount(
    shareCounts,
    'localops_share_accept_clicked',
  );
  const taskSheetOpens = getShareCount(
    shareCounts,
    'localops_share_task_sheet_opened',
  );
  const taskAcceptSuccess = getShareCount(
    shareCounts,
    'localops_share_task_accept_succeeded',
  );
  const baseEvidence = buildTaskEvidence({
    fanoutSent,
    emailOpened,
    shareCounts,
    helper,
    acceptedAge,
  });

  if (isCompletedTask(row, shareCounts, completedTaskIds)) {
    return {
      label: 'No action needed',
      reason: 'Completed/verified task signal is present.',
      evidence: actionCreditCompletion
        ? 'Action Credit row: yes / Verified completion'
        : shareCounts
        ? `completed: ${formatCount(getShareCount(shareCounts, 'localops_task_completion_verified'))} / Action Credit: ${formatCount(getShareCount(shareCounts, 'localops_action_credit_awarded'))}`
        : `status: ${row?.status || '--'}`,
    };
  }

  if (isAcceptedTask(row)) {
    if (acceptedAge !== null && acceptedAge >= 24) {
      return {
        label: 'Monitor accepted task',
        reason: 'Accepted task is older than 24h without completion proof.',
        evidence: baseEvidence,
      };
    }
    return {
      label: 'Watch',
      reason: 'Accepted task has no completion proof yet.',
      evidence: baseEvidence,
    };
  }

  if (isOpenTask(row) && shareCounts && taskSheetOpens > 0 && taskAcceptSuccess === 0) {
    return {
      label: 'Consider raise pay',
      reason: 'Helpers reached the task sheet but no backend-confirmed accept landed.',
      evidence: `task sheet opens: ${formatCount(taskSheetOpens)} / accept success: 0`,
    };
  }

  if (shareCounts && shareLandings > 0 && landingAcceptClicks === 0) {
    return {
      label: 'Share again',
      reason: 'Share traffic has not produced accept intent.',
      evidence: `share landings: ${formatCount(shareLandings)} / accept clicks: 0`,
    };
  }

  if (isOpenTask(row) && fanoutSent !== null && fanoutSent > 0) {
    return {
      label: 'Renotify helpers',
      reason: 'Funded/open task has fanout but remains unassigned.',
      evidence: baseEvidence,
    };
  }

  if (isOpenTask(row) && (fanoutSent === null || fanoutSent === 0)) {
    return {
      label: 'Check fanout',
      reason: 'Funded/open task has no visible helper fanout proof.',
      evidence:
        fanoutSent === null
          ? 'fanout sent: not tracked yet'
          : 'fanout sent: 0',
    };
  }

  return {
    label: 'Need more data',
    reason: 'Existing payload does not prove a safe recommendation.',
    evidence: baseEvidence,
  };
};

const derivePlayerUsefulness = (row, now) => {
  const actionCredit = getTrackedMetricNumber(row?.actionCreditCount);
  const magicCompletions = getTrackedMetricNumber(row?.magicLink?.loginCount);
  const activationSends = getTrackedMetricNumber(row?.activationEmail?.sendCount);
  const location = String(row?.locationStatus || '').toLowerCase();
  const role = String(row?.role || '').toLowerCase();
  const createdAge = hoursSince(row?.createdAt, now);
  const evidence = [
    actionCredit !== null ? `Action Credit: ${formatCount(actionCredit)}` : '',
    magicCompletions !== null
      ? `magic completed: ${formatCount(magicCompletions)}`
      : '',
    activationSends !== null ? `activation sends: ${formatCount(activationSends)}` : '',
    row?.locationStatus ? `location: ${row.locationStatus}` : '',
  ]
    .filter(Boolean)
    .join(' / ');

  if (role.includes('owner') || role.includes('creator')) {
    return { label: 'Owner only', evidence: evidence || `role: ${row?.role}` };
  }
  if (actionCredit !== null && actionCredit > 0) {
    return { label: 'Verified helper', evidence };
  }
  if (createdAge !== null && createdAge <= 24 * 7) {
    return { label: 'New helper', evidence: evidence || `age: ${formatAgeHours(createdAge)}` };
  }
  if (location.includes('missing')) {
    return { label: 'Needs location', evidence: evidence || 'location: missing' };
  }
  if (magicCompletions !== null && magicCompletions > 0) {
    return { label: 'Active helper', evidence };
  }
  if (location.includes('saved') || location.includes('ready')) {
    return { label: 'Map-ready', evidence };
  }
  return { label: 'Not enough data', evidence: evidence || 'Need more data' };
};

const deriveSourceOutcome = (row) => {
  const counts = row?.counts || {};
  const landings = toSafeNumber(counts.localops_share_landing_viewed);
  const acceptClicks = toSafeNumber(counts.localops_share_accept_clicked);
  const taskSheetOpens = toSafeNumber(counts.localops_share_task_sheet_opened);
  const acceptSuccess = toSafeNumber(counts.localops_share_task_accept_succeeded);
  const completions = toSafeNumber(counts.localops_task_completion_verified);
  const actionCredit = toSafeNumber(counts.localops_action_credit_awarded);
  const evidence = `landings: ${formatCount(landings)} / accepts: ${formatCount(acceptSuccess)} / completed: ${formatCount(completions)} / Action Credit: ${formatCount(actionCredit)}`;

  if (completions > 0 || actionCredit > 0) {
    return { label: 'Converts to completion', evidence };
  }
  if (acceptSuccess > 0) return { label: 'Converts to accept', evidence };
  if (acceptClicks > 0 || taskSheetOpens > 0) {
    return { label: 'High intent', evidence };
  }
  if (landings > 0) return { label: 'Traffic only', evidence };
  if (toSafeNumber(row?.total) > 0) {
    return {
      label: 'Low signal',
      evidence: `${formatCount(row.total)} tracked events / no downstream outcome proof`,
    };
  }
  return { label: 'Not enough data', evidence: 'Need more data' };
};

const buildSourceOutcomeRows = (shareFunnelRows = [], operatorSourceRows = []) => {
  if (shareFunnelRows.length) {
    return shareFunnelRows.map((row) => {
      const outcome = deriveSourceOutcome(row);
      return {
        key: row.sourceLabel,
        displayLabel: row.displayLabel || row.sourceLabel,
        total: row.total,
        outcomeLabel: outcome.label,
        evidence: outcome.evidence,
      };
    });
  }
  return operatorSourceRows.map((row) => {
    const outcome = deriveSourceOutcome(row);
    return {
      key: row.sourceKey,
      displayLabel: row.sourceKey,
      total: row.total,
      outcomeLabel: outcome.label,
      evidence: outcome.evidence,
    };
  });
};

const getBestConvertingSource = (sourceOutcomeRows = []) =>
  sourceOutcomeRows
    .filter((row) =>
      ['Converts to completion', 'Converts to accept'].includes(
        row.outcomeLabel,
      ),
    )
    .sort((a, b) => {
      const score = (row) => {
        if (row.outcomeLabel === 'Converts to completion') return 3;
        if (row.outcomeLabel === 'Converts to accept') return 2;
        return 0;
      };
      return score(b) - score(a) || toSafeNumber(b.total) - toSafeNumber(a.total);
    })[0];

const FUNNEL_BREAK_STEPS = [
  {
    label: 'Share interest, no accept intent',
    from: 'localops_share_landing_viewed',
    to: 'localops_share_accept_clicked',
    fromLabel: 'share landings',
    toLabel: 'accept clicks',
  },
  {
    label: 'Continue/auth handoff drop',
    from: 'localops_share_continue_started',
    to: 'localops_share_magic_link_completed',
    fromLabel: 'continue started',
    toLabel: 'magic completed',
  },
  {
    label: 'PlayerMap/task focus drop',
    from: 'localops_share_playermap_arrived',
    to: 'localops_share_task_sheet_opened',
    fromLabel: 'PlayerMap arrivals',
    toLabel: 'task sheet opens',
  },
  {
    label: 'Task terms/pay drop',
    from: 'localops_share_task_accept_clicked',
    to: 'localops_share_task_accept_succeeded',
    fromLabel: 'sheet accept clicks',
    toLabel: 'accept success',
  },
  {
    label: 'Completion risk',
    from: 'localops_share_task_accept_succeeded',
    to: 'localops_task_completion_verified',
    fromLabel: 'accept success',
    toLabel: 'completed',
  },
  {
    label: 'Credit join issue',
    from: 'localops_task_completion_verified',
    to: 'localops_action_credit_awarded',
    fromLabel: 'completed',
    toLabel: 'Action Credit',
  },
];

const deriveWeakestFunnelBreak = (shareFunnelRows = []) => {
  const counts = aggregateShareCounts(shareFunnelRows);
  return FUNNEL_BREAK_STEPS.map((step) => {
    const from = toSafeNumber(counts[step.from]);
    const to = toSafeNumber(counts[step.to]);
    if (from <= 0) return null;
    return {
      ...step,
      from,
      to,
      rate: to / from,
      drop: from - to,
      evidence: `${step.fromLabel}: ${formatCount(from)} / ${step.toLabel}: ${formatCount(to)}`,
    };
  })
    .filter((step) => step && step.drop > 0)
    .sort((a, b) => a.rate - b.rate || b.drop - a.drop)[0];
};

const buildEntityTitleLookup = (operatorVisibility = {}) => {
  const lookup = new Map();
  (operatorVisibility.taskRows || []).forEach((row) => {
    if (row?.taskId && row?.title) lookup.set(`task:${row.taskId}`, row.title);
  });
  (operatorVisibility.offeringRows || []).forEach((row) => {
    if (row?.offeringId && row?.title) {
      lookup.set(`offering:${row.offeringId}`, row.title);
    }
  });
  return lookup;
};

const buildPlayerLabelLookup = (operatorVisibility = {}) => {
  const lookup = new Map();
  (operatorVisibility.activePlayerRows || []).forEach((row) => {
    if (row?.playerId) lookup.set(row.playerId, row.handle || row.playerId);
  });
  return lookup;
};

const buildActionCreditRows = (operatorVisibility = {}) => {
  const entityTitleLookup = buildEntityTitleLookup(operatorVisibility);
  const playerLabelLookup = buildPlayerLabelLookup(operatorVisibility);
  return (operatorVisibility.actionCreditRows || []).map((row) => {
    const entityKey = `${row?.entityType || 'entity'}:${row?.entityId || ''}`;
    const title = entityTitleLookup.get(entityKey) || entityKey;
    const playerLabel =
      playerLabelLookup.get(row?.playerId) || row?.playerId || 'unknown player';
    return {
      ...row,
      title,
      meta: [
        row?.entityId,
        row?.role ? `${row.role} ${playerLabel}` : playerLabel,
        '+1 Action Credit',
        'Verified completion',
      ]
        .filter(Boolean)
        .join(' / '),
    };
  });
};

const buildCompletedTaskIdSet = (operatorVisibility = {}) => {
  const taskIds = new Set();
  (operatorVisibility.actionCreditRows || []).forEach((row) => {
    const entityType = String(row?.entityType || '').toLowerCase();
    const entityId = cleanDisplayText(row?.entityId || row?.taskId || '', 120);
    if (entityId && (!entityType || entityType === 'task')) {
      taskIds.add(entityId);
    }
  });
  return taskIds;
};

export const buildOperatorActionability = ({
  operatorVisibility = {},
  shareFunnelRows = [],
  now = new Date(),
} = {}) => {
  const safeOperatorVisibility = operatorVisibility || {};
  const shareCountsByTaskId = buildShareCountsByTaskId(shareFunnelRows);
  const completedTaskIds = buildCompletedTaskIdSet(safeOperatorVisibility);
  const taskRows = (safeOperatorVisibility.taskRows || []).map((row) => {
    const shareCounts = shareCountsByTaskId.get(String(row?.taskId || ''));
    return {
      ...row,
      actionability: deriveTaskActionability(
        row,
        shareCounts,
        now,
        completedTaskIds,
      ),
      shareCounts,
    };
  });
  const playerRows = (safeOperatorVisibility.activePlayerRows || []).map((row) => ({
    ...row,
    usefulness: derivePlayerUsefulness(row, now),
  }));
  const sourceRows = buildSourceOutcomeRows(
    shareFunnelRows,
    safeOperatorVisibility.sourceAttributionRows || [],
  );
  const bestSource = getBestConvertingSource(sourceRows);
  const weakestFunnelBreak = deriveWeakestFunnelBreak(shareFunnelRows);
  const actionCreditRows = buildActionCreditRows(safeOperatorVisibility);
  const attentionTasks = taskRows.filter((row) =>
    ['Renotify helpers', 'Consider raise pay', 'Share again', 'Check fanout', 'Monitor accepted task'].includes(
      row.actionability.label,
    ),
  );
  const fanoutNoAcceptTasks = taskRows.filter((row) => {
    const fanoutSent = getTrackedMetricNumber(row?.fanout?.emailSentCount);
    return (
      fanoutSent !== null &&
      fanoutSent > 0 &&
      !isAcceptedTask(row) &&
      !isCompletedTask(row, row.shareCounts, completedTaskIds)
    );
  });
  const emailOpenedNoActionTasks = taskRows.filter((row) => {
    const opened = getTrackedMetricNumber(row?.fanout?.emailPixelOpens);
    return (
      opened !== null &&
      opened > 0 &&
      !isAcceptedTask(row) &&
      !isCompletedTask(row, row.shareCounts, completedTaskIds)
    );
  });
  const shareInterestNoAccept = shareFunnelRows.filter((row) => {
    const counts = row?.counts || {};
    return (
      toSafeNumber(counts.localops_share_landing_viewed) > 0 &&
      toSafeNumber(counts.localops_share_task_accept_succeeded) === 0
    );
  });
  const acceptedAgingTasks = taskRows.filter(
    (row) => row.actionability.label === 'Monitor accepted task',
  );
  const completedTasks = taskRows.filter(
    (row) => row.actionability.label === 'No action needed',
  );
  const usefulPlayers = playerRows.filter((row) =>
    ['Verified helper', 'Active helper', 'Map-ready', 'New helper'].includes(
      row.usefulness.label,
    ),
  );

  return {
    taskRows,
    playerRows,
    sourceRows,
    actionCreditRows,
    weakestFunnelBreak,
    triageCards: [
      {
        title: 'Tasks needing attention',
        count: attentionTasks.length,
        label: attentionTasks[0]?.title || attentionTasks[0]?.taskId || 'Need more data',
        action: attentionTasks[0]?.actionability.label || 'Need more data',
        reason: attentionTasks[0]?.actionability.reason || 'No supported task recommendation.',
        evidence: attentionTasks[0]?.actionability.evidence || 'Need more data',
      },
      {
        title: 'Fanout sent, no accept',
        count: fanoutNoAcceptTasks.length,
        label: fanoutNoAcceptTasks[0]?.title || fanoutNoAcceptTasks[0]?.taskId || 'Need more data',
        action: fanoutNoAcceptTasks.length ? 'Renotify helpers' : 'Need more data',
        reason: fanoutNoAcceptTasks.length
          ? 'Helper fanout exists but the task is still unassigned.'
          : 'No supported fanout/no-accept task in the current payload.',
        evidence:
          fanoutNoAcceptTasks[0]?.actionability.evidence || 'Need more data',
      },
      {
        title: 'Email opened, no downstream action',
        count: emailOpenedNoActionTasks.length,
        label: emailOpenedNoActionTasks[0]?.title || emailOpenedNoActionTasks[0]?.taskId || 'Need more data',
        action: emailOpenedNoActionTasks.length ? 'Watch' : 'Need more data',
        reason: emailOpenedNoActionTasks.length
          ? 'Email open proof exists but no helper accept is visible.'
          : 'No supported email-open drop in the current payload.',
        evidence:
          emailOpenedNoActionTasks[0]?.actionability.evidence || 'Need more data',
      },
      {
        title: 'Share interest, no accept',
        count: shareInterestNoAccept.length,
        label: shareInterestNoAccept[0]?.displayLabel || 'Need more data',
        action: shareInterestNoAccept.length ? 'Share again' : 'Need more data',
        reason: shareInterestNoAccept.length
          ? 'Share traffic exists without backend-confirmed accept.'
          : 'Share rows do not prove this drop.',
        evidence: shareInterestNoAccept[0]
          ? `share landings: ${formatCount(shareInterestNoAccept[0].counts?.localops_share_landing_viewed)} / accept success: ${formatCount(shareInterestNoAccept[0].counts?.localops_share_task_accept_succeeded)}`
          : 'Need more data',
      },
      {
        title: 'Accepted task aging',
        count: acceptedAgingTasks.length,
        label: acceptedAgingTasks[0]?.title || acceptedAgingTasks[0]?.taskId || 'Need more data',
        action: acceptedAgingTasks.length ? 'Monitor accepted task' : 'Need more data',
        reason: acceptedAgingTasks[0]?.actionability.reason || 'No accepted task age risk proven.',
        evidence: acceptedAgingTasks[0]?.actionability.evidence || 'Need more data',
      },
      {
        title: 'Completed/verified tasks',
        count: completedTasks.length,
        label: completedTasks[0]?.title || completedTasks[0]?.taskId || 'Need more data',
        action: completedTasks.length ? 'No action needed' : 'Need more data',
        reason: completedTasks.length
          ? 'Completion or Action Credit proof is visible.'
          : 'No completed task proof in the current rows.',
        evidence: completedTasks[0]?.actionability.evidence || 'Need more data',
      },
      {
        title: 'Best converting source',
        count: bestSource ? 1 : 0,
        label: bestSource?.displayLabel || 'Need more data',
        action: bestSource ? 'Watch' : 'Need more data',
        reason: bestSource
          ? bestSource.outcomeLabel
          : 'No source has accept/completion/Action Credit proof.',
        evidence: bestSource?.evidence || 'Need more data',
      },
      {
        title: 'Weakest funnel step',
        count: weakestFunnelBreak ? weakestFunnelBreak.drop : 0,
        label: weakestFunnelBreak?.label || 'Need more data',
        action: weakestFunnelBreak ? 'Watch' : 'Need more data',
        reason: weakestFunnelBreak
          ? 'Largest supported conversion drop in the share funnel.'
          : 'Need share funnel transition data.',
        evidence: weakestFunnelBreak?.evidence || 'Need more data',
      },
      {
        title: 'Useful helper/player signal',
        count: usefulPlayers.length,
        label: usefulPlayers[0]?.handle || usefulPlayers[0]?.playerId || 'Need more data',
        action: usefulPlayers.length ? 'Watch' : 'Need more data',
        reason: usefulPlayers[0]?.usefulness.label || 'No useful helper signal proven.',
        evidence: usefulPlayers[0]?.usefulness.evidence || 'Need more data',
      },
    ],
  };
};

const ActionabilityNote = ({ actionability }) =>
  actionability ? (
    <div className="ops-health-actionability-note">
      <strong>{actionability.label}</strong>
      <span>{actionability.reason}</span>
      <small>{actionability.evidence}</small>
    </div>
  ) : null;

const PlayerUsefulnessNote = ({ usefulness }) =>
  usefulness ? (
    <div className="ops-health-actionability-note">
      <strong>{usefulness.label}</strong>
      <small>{usefulness.evidence}</small>
    </div>
  ) : null;

const TriageCard = ({ card }) => (
  <article className="ops-health-triage-card">
    <div>
      <strong>{card.title}</strong>
      <span>{formatCount(card.count)}</span>
    </div>
    <p>{card.label}</p>
    <small>{card.reason}</small>
    <em>{card.action}</em>
    <small>{card.evidence}</small>
  </article>
);

const JsonDetails = ({ title, payload, testId }) => (
  <details className="ops-health-json" data-testid={testId}>
    <summary>{title}</summary>
    <pre>{JSON.stringify(payload || {}, null, 2)}</pre>
  </details>
);

export default function LocalOpsOpsHealth() {
  const { player } = useContext(AuthContext);
  const { socket } = useSocket() || {};
  const allowed = canViewLocalOpsOps(player);
  const [readiness, setReadiness] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [taskFilters, setTaskFilters] = useState(() =>
    buildDefaultTaskFinderFilters(),
  );
  const [taskResults, setTaskResults] = useState([]);
  const [taskReport, setTaskReport] = useState(null);
  const [settlementRows, setSettlementRows] = useState([]);
  const [taskId, setTaskId] = useState('');
  const [loading, setLoading] = useState(false);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementActionBusy, setSettlementActionBusy] = useState('');
  const [error, setError] = useState('');
  const [taskSearchError, setTaskSearchError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [settlementError, setSettlementError] = useState('');
  const [settlementNotice, setSettlementNotice] = useState('');
  const [launchAttribution, setLaunchAttribution] = useState(null);
  const launchAttributionRef = useRef(null);
  const launchVisitBaselineRef = useRef({
    initialized: false,
    snapshot: null,
    hadSavedBaseline: false,
  });
  const launchAttributionRefreshTimerRef = useRef(null);
  const launchAttributionPendingWhileHiddenRef = useRef(false);
  const [launchAttributionLoading, setLaunchAttributionLoading] = useState(false);
  const [launchAttributionError, setLaunchAttributionError] = useState('');
  const [launchRealtimeStatus, setLaunchRealtimeStatus] = useState('manual');
  const [launchDeltaItems, setLaunchDeltaItems] = useState([]);
  const [launchLiveDeltaItems, setLaunchLiveDeltaItems] = useState([]);
  const [launchDeltaReason, setLaunchDeltaReason] = useState('');
  const [launchBaselineStatus, setLaunchBaselineStatus] = useState('idle');
  const [launchAttributionRefreshedAt, setLaunchAttributionRefreshedAt] =
    useState(null);
  const [operatorVisibility, setOperatorVisibility] = useState(null);
  const operatorVisibilityRef = useRef(null);
  const operatorVisitBaselineRef = useRef({
    initialized: false,
    snapshot: null,
    hadSavedBaseline: false,
  });
  const [operatorVisibilityLoading, setOperatorVisibilityLoading] =
    useState(false);
  const [operatorVisibilityError, setOperatorVisibilityError] = useState('');
  const [operatorVisibilityRefreshedAt, setOperatorVisibilityRefreshedAt] =
    useState(null);
  const [operatorDeltaItems, setOperatorDeltaItems] = useState([]);
  const [operatorLiveDeltaItems, setOperatorLiveDeltaItems] = useState([]);
  const [conciergeForm, setConciergeForm] = useState(() =>
    buildDefaultConciergeIntakeForm(),
  );
  const [conciergeSending, setConciergeSending] = useState(false);
  const [conciergeError, setConciergeError] = useState('');
  const [conciergeResult, setConciergeResult] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );
  const launchSnapshotKey = useMemo(
    () =>
      buildOpsHealthSnapshotKey({
        reportType: 'launchAttribution',
        filters: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
      }),
    [],
  );
  const operatorSnapshotKey = useMemo(
    () =>
      buildOpsHealthSnapshotKey({
        reportType: 'operatorVisibility',
        filters: { marketSlug: 'camden' },
      }),
    [],
  );

  useEffect(() => {
    if (!allowed) return;
    try {
      const remembered = window.localStorage.getItem(
        LOCALOPS_OPS_LAST_TASK_ID_KEY,
      );
      if (remembered) setTaskId((current) => current || remembered);
    } catch (err) {
      // Ignore localStorage issues; this page should still run diagnostics.
    }
  }, [allowed]);

  const refreshReports = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const [readinessRes, rollupRes] = await Promise.all([
        axiosConn.get('local-ops/debug/readiness', authConfig),
        axiosConn.get('local-ops/debug/accounting/rollup', authConfig),
      ]);
      setReadiness(readinessRes?.data?.data || readinessRes?.data || null);
      setRollup(rollupRes?.data?.data || rollupRes?.data || null);
      setLastCheckedAt(new Date().toISOString());
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load LocalOps ops health.',
      );
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchSettlementQueue = useCallback(async () => {
    if (!allowed) return;
    setSettlementLoading(true);
    setSettlementError('');
    try {
      const res = await axiosConn.get(
        'local-ops/debug/settlements/orders',
        authConfig,
      );
      const payload = res?.data?.data || res?.data || {};
      setSettlementRows(payload.rows || []);
    } catch (err) {
      setSettlementRows([]);
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load offering settlement queue.',
      );
    } finally {
      setSettlementLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchLaunchAttribution = useCallback(async (options = {}) => {
    if (!allowed) return;
    setLaunchAttributionLoading(true);
    setLaunchAttributionError('');
    try {
      const res = await axiosConn.get(LAUNCH_ATTRIBUTION_ENDPOINT, {
        ...authConfig,
        params: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
      });
      const nextReport = res?.data?.data || res?.data || null;
      const previousRenderedSnapshot = launchAttributionRef.current
        ? buildLaunchAttributionSnapshot(launchAttributionRef.current)
        : null;
      const nextSnapshot = buildLaunchAttributionSnapshot(nextReport);
      const liveDeltas = previousRenderedSnapshot
        ? markLiveDeltas(
            buildLaunchAttributionDeltas(previousRenderedSnapshot, nextSnapshot),
          )
        : [];
      const baselineState = launchVisitBaselineRef.current;
      const wasInitialized = baselineState.initialized;
      if (!wasInitialized) {
        const savedSnapshot = readOpsHealthSnapshot(launchSnapshotKey)?.snapshot;
        launchVisitBaselineRef.current = {
          initialized: true,
          snapshot: savedSnapshot || nextSnapshot,
          hadSavedBaseline: !!savedSnapshot,
        };
      }
      const currentBaseline = launchVisitBaselineRef.current;
      const shouldShowDeltas =
        wasInitialized || currentBaseline.hadSavedBaseline;
      const deltas = shouldShowDeltas
        ? buildLaunchAttributionDeltas(currentBaseline.snapshot, nextSnapshot)
        : [];
      launchAttributionRef.current = nextReport;
      setLaunchAttribution(nextReport);
      setLaunchAttributionRefreshedAt(new Date().toISOString());
      setLaunchLiveDeltaItems(liveDeltas);
      if (shouldShowDeltas && deltas.length) {
        setLaunchDeltaItems(deltas);
        setLaunchDeltaReason(options.reason || 'report refresh');
        setLaunchBaselineStatus('deltas');
      } else if (shouldShowDeltas) {
        setLaunchDeltaItems([]);
        setLaunchDeltaReason('');
        setLaunchBaselineStatus('no_changes');
      } else {
        setLaunchDeltaItems([]);
        setLaunchDeltaReason('');
        setLaunchBaselineStatus('baseline_saved');
      }
      writeOpsHealthSnapshot(launchSnapshotKey, nextSnapshot);
    } catch (err) {
      setLaunchAttribution(null);
      launchAttributionRef.current = null;
      setLaunchLiveDeltaItems([]);
      setLaunchAttributionError('Unable to load launch attribution.');
    } finally {
      setLaunchAttributionLoading(false);
    }
  }, [allowed, authConfig, launchSnapshotKey]);

  const fetchOperatorVisibility = useCallback(async () => {
    if (!allowed) return;
    setOperatorVisibilityLoading(true);
    setOperatorVisibilityError('');
    try {
      const res = await axiosConn.get(OPERATOR_VISIBILITY_ENDPOINT, {
        ...authConfig,
        params: { market: 'camden' },
      });
      const nextReport = res?.data?.data || res?.data || null;
      const previousRenderedSnapshot = operatorVisibilityRef.current
        ? buildOperatorVisibilitySnapshot(operatorVisibilityRef.current)
        : null;
      const nextSnapshot = buildOperatorVisibilitySnapshot(nextReport);
      const liveDeltas = previousRenderedSnapshot
        ? markLiveDeltas(
            buildOperatorVisibilityDeltas(previousRenderedSnapshot, nextSnapshot),
          )
        : [];
      const baselineState = operatorVisitBaselineRef.current;
      const wasInitialized = baselineState.initialized;
      if (!wasInitialized) {
        const savedSnapshot =
          readOpsHealthSnapshot(operatorSnapshotKey)?.snapshot;
        operatorVisitBaselineRef.current = {
          initialized: true,
          snapshot: savedSnapshot || nextSnapshot,
          hadSavedBaseline: !!savedSnapshot,
        };
      }
      const currentBaseline = operatorVisitBaselineRef.current;
      const shouldShowDeltas =
        wasInitialized || currentBaseline.hadSavedBaseline;
      const deltas = shouldShowDeltas
        ? buildOperatorVisibilityDeltas(
            currentBaseline.snapshot,
            nextSnapshot,
          )
        : [];
      operatorVisibilityRef.current = nextReport;
      setOperatorVisibility(nextReport);
      setOperatorVisibilityRefreshedAt(new Date().toISOString());
      setOperatorDeltaItems(shouldShowDeltas ? deltas : []);
      setOperatorLiveDeltaItems(liveDeltas);
      writeOpsHealthSnapshot(operatorSnapshotKey, nextSnapshot);
    } catch (err) {
      setOperatorVisibility(null);
      operatorVisibilityRef.current = null;
      setOperatorDeltaItems([]);
      setOperatorLiveDeltaItems([]);
      setOperatorVisibilityError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load operator visibility.',
      );
    } finally {
      setOperatorVisibilityLoading(false);
    }
  }, [allowed, authConfig, operatorSnapshotKey]);

  useEffect(() => {
    if (!launchLiveDeltaItems.length) return undefined;
    const timer = window.setTimeout(() => {
      setLaunchLiveDeltaItems([]);
    }, LIVE_DELTA_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [launchLiveDeltaItems]);

  useEffect(() => {
    if (!operatorLiveDeltaItems.length) return undefined;
    const timer = window.setTimeout(() => {
      setOperatorLiveDeltaItems([]);
    }, LIVE_DELTA_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [operatorLiveDeltaItems]);

  useEffect(() => {
    if (!allowed || !socket?.on || !socket?.off || !socket?.emit) {
      setLaunchRealtimeStatus('manual');
      return undefined;
    }

    let active = true;
    const subscribe = () => {
      if (!socket.connected) {
        setLaunchRealtimeStatus('manual');
        return;
      }
      socket.emit(
        'localops:ops:subscribe_launch_attribution',
        CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
        (ack = {}) => {
          if (!active) return;
          setLaunchRealtimeStatus(ack?.success ? 'live' : 'manual');
        },
      );
    };
    const scheduleRefresh = (payload = {}) => {
      const marketSlug = String(payload?.marketSlug || '').trim();
      if (marketSlug && marketSlug !== CAMDEN_LAUNCH_ATTRIBUTION_PARAMS.marketSlug) {
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) {
        launchAttributionPendingWhileHiddenRef.current = true;
        return;
      }
      window.clearTimeout(launchAttributionRefreshTimerRef.current);
      launchAttributionRefreshTimerRef.current = window.setTimeout(() => {
        fetchLaunchAttribution({
          reason: payload?.reason || 'socket invalidation',
        });
        fetchOperatorVisibility();
      }, 900);
    };
    const handleConnect = () => subscribe();
    const handleDisconnect = () => setLaunchRealtimeStatus('manual');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('localops:launch_attribution_changed', scheduleRefresh);
    subscribe();

    return () => {
      active = false;
      window.clearTimeout(launchAttributionRefreshTimerRef.current);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('localops:launch_attribution_changed', scheduleRefresh);
    };
  }, [allowed, fetchLaunchAttribution, fetchOperatorVisibility, socket]);

  useEffect(() => {
    if (!allowed) return undefined;
    const handleVisibilityChange = () => {
      if (
        document.hidden ||
        !launchAttributionPendingWhileHiddenRef.current
      ) {
        return;
      }
      launchAttributionPendingWhileHiddenRef.current = false;
      fetchLaunchAttribution({ reason: 'focus refresh' });
      fetchOperatorVisibility();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [allowed, fetchLaunchAttribution, fetchOperatorVisibility]);

  const fetchTaskResults = useCallback(
    async (filters) => {
      if (!allowed) return;
      setTaskSearchLoading(true);
      setTaskSearchError('');
      try {
        const res = await axiosConn.get('local-ops/debug/accounting/tasks', {
          ...authConfig,
          params: buildTaskFinderParams(filters),
        });
        const payload = res?.data?.data || res?.data || {};
        setTaskResults(payload.tasks || payload.rows || []);
      } catch (err) {
        setTaskResults([]);
        setTaskSearchError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to find recent LocalOps tasks.',
        );
      } finally {
        setTaskSearchLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const refreshAll = useCallback(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution();
    fetchTaskResults(taskFilters);
    fetchOperatorVisibility();
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
    taskFilters,
  ]);

  useEffect(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution();
    fetchTaskResults(taskFilters);
    fetchOperatorVisibility();
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
  ]);

  const loadTaskReport = useCallback(
    async (nextTaskId) => {
      if (!allowed) return;
      const trimmedTaskId = String(nextTaskId || '').trim();
      if (!trimmedTaskId) {
        setTaskError('Enter a task id.');
        return;
      }

      setTaskId(trimmedTaskId);
      setTaskLoading(true);
      setTaskError('');
      try {
        const res = await axiosConn.get(
          `local-ops/debug/accounting/task/${encodeURIComponent(trimmedTaskId)}`,
          authConfig,
        );
        setTaskReport(res?.data?.data || res?.data || null);
        try {
          window.localStorage.setItem(
            LOCALOPS_OPS_LAST_TASK_ID_KEY,
            trimmedTaskId,
          );
        } catch (err) {
          // Last-task memory is optional and must never block the report.
        }
      } catch (err) {
        setTaskReport(null);
        setTaskError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to load task accounting report.',
        );
      } finally {
        setTaskLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const handleTaskSearch = (event) => {
    event.preventDefault();
    if (!allowed) return;
    fetchTaskResults(taskFilters);
  };

  const handleTaskLookup = async (event) => {
    event.preventDefault();
    await loadTaskReport(taskId);
  };

  const updateTaskFilter = (field, value) => {
    setTaskFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSettlementAction = async (row, action) => {
    if (!allowed || !row?.orderId) return;
    const isRefund = action === 'refund_buyer';
    const confirmation = isRefund
      ? 'Refund buyer? This returns held JGK to the buyer and restores finite offering quantity if applicable.'
      : 'Pay seller? This releases held JGK to the seller and marks the order fulfilled.';
    const confirmed = window.confirm(confirmation);
    if (!confirmed) return;

    setSettlementActionBusy(`${row.orderId}:${action}`);
    setSettlementError('');
    setSettlementNotice('');
    try {
      await axiosConn.post(
        `local-ops/debug/settlements/orders/${encodeURIComponent(
          row.orderId,
        )}/resolve`,
        {
          action,
          reason: 'ops_health_settlement_queue',
        },
        authConfig,
      );
      setSettlementNotice(
        isRefund
          ? 'Buyer refunded; held JGK returned.'
          : 'Seller paid; order marked fulfilled.',
      );
      await fetchSettlementQueue();
      await fetchOperatorVisibility();
    } catch (err) {
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to resolve this settlement.',
      );
    } finally {
      setSettlementActionBusy('');
    }
  };

  const updateConciergeForm = (field, value) => {
    setConciergeForm((current) => ({
      ...current,
      [field]: value,
    }));
    setConciergeError('');
  };

  const handleConciergeSubmit = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    const deliveryAddress = conciergeForm.deliveryAddress.trim();
    const customerEmail = conciergeForm.customerEmail.trim();
    if (!deliveryAddress) {
      setConciergeError('Destination is required.');
      return;
    }
    if (!customerEmail) {
      setConciergeError('Customer email is required.');
      return;
    }

    setConciergeSending(true);
    setConciergeError('');
    setConciergeResult(null);
    try {
      const res = await axiosConn.post(
        CONCIERGE_INTAKE_ENDPOINT,
        {
          intakeType: 'collision_specialist_ride',
          partnerSource: 'collision-specialist-camden',
          customerEmail,
          customerPhone: conciergeForm.customerPhone.trim() || undefined,
          deliveryAddress,
          notes: conciergeForm.notes.trim() || undefined,
        },
        authConfig,
      );
      setConciergeResult(res?.data?.data || res?.data || {});
      await fetchOperatorVisibility();
      setConciergeForm((current) => ({
        ...buildDefaultConciergeIntakeForm(),
        customerPhone: current.customerPhone,
      }));
    } catch (err) {
      setConciergeError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to send the concierge magic link.',
      );
    } finally {
      setConciergeSending(false);
    }
  };

  const anomalies = useMemo(
    () => collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }),
    [readiness, rollup, taskReport],
  );
  const opsStatus = deriveLocalOpsOpsStatus({
    readiness,
    rollup,
    anomalies,
    error,
  });
  const launchTotalEvents = getTotalLaunchEvents(launchAttribution);
  const launchVisitTotalEvents = getTotalLaunchVisitEvents(launchAttribution);
  const launchAuthTotalEvents = getTotalLaunchAuthEvents(launchAttribution);
  const launchMagicLinkPlayerActionTotalEvents =
    getTotalMagicLinkPlayerActionEvents(launchAttribution);
  const launchShareFunnelTotalEvents =
    getTotalShareFunnelEvents(launchAttribution);
  const launchSourceRows = useMemo(
    () => buildLaunchSourceRows(launchAttribution),
    [launchAttribution],
  );
  const launchVisitSourceRows = useMemo(
    () => buildVisitSourceRows(launchAttribution),
    [launchAttribution],
  );
  const launchAuthSourceRows = useMemo(
    () => buildAuthSourceRows(launchAttribution),
    [launchAttribution],
  );
  const magicLinkPlayerActionRows = useMemo(
    () => buildMagicLinkPlayerActionRows(launchAttribution),
    [launchAttribution],
  );
  const operatorSummary = operatorVisibility?.systemSummary || {};
  const operatorActivationFunnel = operatorVisibility?.activationFunnel || {};
  const operatorPlayerRows = operatorVisibility?.activePlayerRows || [];
  const operatorTaskRows = operatorVisibility?.taskRows || [];
  const operatorOfferingRows = operatorVisibility?.offeringRows || [];
  const operatorSourceRows = operatorVisibility?.sourceAttributionRows || [];
  const operatorEmailRows =
    operatorVisibility?.emailNotificationProofRows || [];
  const operatorLocationReadiness =
    operatorVisibility?.locationMapReadiness || {};
  const operatorActionCreditRows = operatorVisibility?.actionCreditRows || [];
  const operatorLocPlaceholder =
    operatorVisibility?.locReadinessPlaceholder || {};
  const operatorEntityTitleLookup = useMemo(() => {
    const lookup = new Map();
    operatorTaskRows.forEach((row) => {
      if (row?.taskId && row?.title) lookup.set(`task:${row.taskId}`, row.title);
    });
    operatorOfferingRows.forEach((row) => {
      if (row?.offeringId && row?.title) {
        lookup.set(`offering:${row.offeringId}`, row.title);
      }
    });
    return lookup;
  }, [operatorOfferingRows, operatorTaskRows]);
  const getOperatorEmailEntityTitle = useCallback(
    (row) => {
      const entityType = row?.entityType || (row?.taskId ? 'task' : '');
      const entityId = row?.entityId || row?.taskId || row?.offeringId || '';
      const lookupTitle =
        entityType && entityId
          ? operatorEntityTitleLookup.get(`${entityType}:${entityId}`)
          : '';
      return (
        row?.entityTitle ||
        row?.taskTitle ||
        row?.offeringTitle ||
        lookupTitle ||
        ''
      );
    },
    [operatorEntityTitleLookup],
  );
  const shareFunnelTaskTitleLookup = useMemo(
    () =>
      buildTaskTitleLookup({
        launchAttribution,
        operatorOfferingRows,
        operatorTaskRows,
        taskResults,
        taskReport,
      }),
    [
      launchAttribution,
      operatorOfferingRows,
      operatorTaskRows,
      taskResults,
      taskReport,
    ],
  );
  const shareFunnelRows = useMemo(
    () => buildShareFunnelRows(launchAttribution, shareFunnelTaskTitleLookup),
    [launchAttribution, shareFunnelTaskTitleLookup],
  );
  const operatorActionability = useMemo(
    () =>
      buildOperatorActionability({
        operatorVisibility,
        shareFunnelRows,
        now: operatorVisibility?.generatedAt
          ? new Date(operatorVisibility.generatedAt)
          : new Date(),
      }),
    [operatorVisibility, shareFunnelRows],
  );
  const launchDeltaLookup = useMemo(
    () => buildDeltaLookup([...launchDeltaItems, ...launchLiveDeltaItems]),
    [launchDeltaItems, launchLiveDeltaItems],
  );
  const operatorDeltaLookup = useMemo(
    () => buildDeltaLookup([...operatorDeltaItems, ...operatorLiveDeltaItems]),
    [operatorDeltaItems, operatorLiveDeltaItems],
  );

  if (!allowed) {
    return (
      <main className="ops-health-page ops-health-page--locked">
        <section className="ops-health-locked" data-testid="ops-health-locked">
          <h1>Ops Health unavailable</h1>
          <p>This internal LocalOps report is not available for this account.</p>
        </section>
      </main>
    );
  }

  const readinessSummary = readiness?.summary || {};
  const staticConfig = readiness?.staticConfig || {};
  const database = readiness?.database || {};
  const stripe = staticConfig?.stripe || {};
  const ignitionFlags = staticConfig?.ignition?.flags || {};
  const systemPlayers = database?.systemPlayers || {};

  return (
    <main className="ops-health-page" data-testid="ops-health-page">
      <header className="ops-health-header">
        <div>
          <p className="ops-health-kicker">LocalOps Internal</p>
          <h1>Ops Health</h1>
        </div>
        <button
          type="button"
          className="ops-health-refresh"
          onClick={refreshAll}
          disabled={
            loading ||
            taskSearchLoading ||
            launchAttributionLoading ||
            operatorVisibilityLoading
          }
        >
          {loading ||
          taskSearchLoading ||
          launchAttributionLoading ||
          operatorVisibilityLoading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </header>

      <section
        className="ops-health-panel ops-health-concierge"
        data-testid="ops-health-concierge-intake"
      >
        <div className="ops-health-section-heading">
          <h2>LocalOps Concierge Intake</h2>
          <span>collision-specialist-camden</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Create a customer-owned ride draft and send the existing magic-link
          handoff. The customer reviews, funds, and publishes the task.
        </p>
        <form
          className="ops-health-intake-form"
          onSubmit={handleConciergeSubmit}
        >
          <label htmlFor="ops-health-intake-partner">Partner source</label>
          <input
            id="ops-health-intake-partner"
            value="Collision Specialist"
            readOnly
          />

          <label htmlFor="ops-health-intake-pickup">Pickup</label>
          <input
            id="ops-health-intake-pickup"
            value={COLLISION_SPECIALIST_PICKUP_ADDRESS}
            readOnly
          />

          <label htmlFor="ops-health-intake-destination">Destination</label>
          <input
            id="ops-health-intake-destination"
            value={conciergeForm.deliveryAddress}
            onChange={(event) =>
              updateConciergeForm('deliveryAddress', event.target.value)
            }
            placeholder="Customer destination address"
            required
          />

          <label htmlFor="ops-health-intake-email">Customer email</label>
          <input
            id="ops-health-intake-email"
            type="email"
            value={conciergeForm.customerEmail}
            onChange={(event) =>
              updateConciergeForm('customerEmail', event.target.value)
            }
            placeholder="customer@example.com"
            required
          />

          <label htmlFor="ops-health-intake-phone">Customer phone</label>
          <input
            id="ops-health-intake-phone"
            value={conciergeForm.customerPhone}
            onChange={(event) =>
              updateConciergeForm('customerPhone', event.target.value)
            }
            placeholder="Optional manual reference"
          />

          <label htmlFor="ops-health-intake-notes">Notes</label>
          <textarea
            id="ops-health-intake-notes"
            value={conciergeForm.notes}
            onChange={(event) =>
              updateConciergeForm('notes', event.target.value)
            }
            placeholder="Optional internal note"
          />

          <div className="ops-health-intake-price">
            <span>Suggested price</span>
            <strong>
              {Number.isFinite(Number(conciergeResult?.suggestedPriceUsd))
                ? formatUsd(conciergeResult.suggestedPriceUsd)
                : 'Auto after destination is resolved'}
            </strong>
          </div>

          <div className="ops-health-intake-actions">
            <button type="submit" disabled={conciergeSending}>
              {conciergeSending ? 'Sending...' : 'Send magic link'}
            </button>
          </div>
        </form>
        {conciergeError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {conciergeError}
          </div>
        ) : null}
        {conciergeResult ? (
          <div
            className="ops-health-alert ops-health-alert--success ops-health-alert--inline"
            data-testid="ops-health-concierge-success"
          >
            <strong>Magic link sent.</strong>
            <p>
              Suggested amount{' '}
              {formatUsd(conciergeResult.suggestedPriceUsd)}. Draft token
              created: {conciergeResult.draftTokenCreated ? 'yes' : 'unknown'}.
            </p>
            {conciergeResult.fallbackReviewLink ? (
              <p>
                Review link:{' '}
                <code data-testid="ops-health-concierge-review-link">
                  {conciergeResult.fallbackReviewLink}
                </code>
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      <section
        className={`ops-health-status ops-health-status--${opsStatus}`}
        data-testid="ops-health-status"
      >
        <div>
          <span className="ops-health-status-label">Overall status</span>
          <strong>{STATUS_LABELS[opsStatus]}</strong>
        </div>
        <Metric label="Last checked" value={formatDate(lastCheckedAt)} />
        <Metric label="Readiness" value={truthyLabel(readinessSummary.pass)} />
        <Metric label="Accounting" value={truthyLabel(rollup?.pass)} />
        <Metric label="Anomalies" value={anomalies.length} />
        <Metric label="Journal entries" value={rollup?.entryCount ?? '--'} />
        <Metric
          label="Rollup period"
          value={
            rollup?.from || rollup?.to
              ? `${rollup?.from || 'start'} to ${rollup?.to || 'now'}`
              : 'All time'
          }
        />
      </section>

      {error ? (
        <section className="ops-health-alert ops-health-alert--error">
          {error}
        </section>
      ) : null}

      <section className="ops-health-panel ops-health-anomalies">
        <div className="ops-health-section-heading">
          <h2>Anomalies</h2>
          <span>{anomalies.length}</span>
        </div>
        {anomalies.length > 0 ? (
          <ul>
            {anomalies.map((item, index) => (
              <li
                key={`${item.source}-${item.code}-${index}`}
                className={`ops-health-anomaly ops-health-anomaly--${item.severity}`}
              >
                <span>{item.severity}</span>
                <strong>{item.code}</strong>
                <p>{item.message}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ops-health-muted">No anomalies reported by the current checks.</p>
        )}
      </section>

      <section
        className="ops-health-panel ops-health-launch-attribution"
        data-testid="ops-health-launch-attribution"
      >
        <div className="ops-health-section-heading">
          <h2>Camden Launch Attribution</h2>
          <span>{launchAttribution?.proofType || 'durable events'}</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Durable attribution from LocalOps events. Canonical task-source
          attribution shows which source created the task. Magic-link player
          actions show which source acquired the player who acted.
        </p>
        <div className="ops-health-launch-pulse" data-testid="ops-health-launch-pulse">
          <span
            className={`ops-health-live-pill ops-health-live-pill--${launchRealtimeStatus}`}
          >
            {launchRealtimeStatus === 'live' ? 'Live' : 'Manual refresh'}
          </span>
          <span>
            Last updated {formatDate(launchAttributionRefreshedAt)}
          </span>
          {launchDeltaItems.length > 0 ? (
            <div
              className="ops-health-launch-delta-board"
              data-testid="ops-health-launch-delta-board"
            >
              <small>Since last Ops Health snapshot</small>
              <ul className="ops-health-delta-list">
                {launchDeltaItems.map((item) => (
                  <li key={item.key}>
                    <strong>{formatDeltaValue(item)}</strong>
                    <span>{item.label}</span>
                    <small>
                      {item.group}
                      {launchDeltaReason ? ` / ${launchDeltaReason}` : ''}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <small>
              {launchBaselineStatus === 'baseline_saved'
                ? 'Baseline saved for next visit.'
                : 'No report deltas since the last snapshot.'}
            </small>
          )}
        </div>
        {launchAttribution?.generatedAt ? (
          <p className="ops-health-mini-meta">
            Generated {formatDate(launchAttribution.generatedAt)}
            {launchAttribution?.filters?.regionSlug
              ? ` / region ${launchAttribution.filters.regionSlug}`
              : ''}
            {launchAttribution?.filters?.marketSlug
              ? ` / market ${launchAttribution.filters.marketSlug}`
              : ''}
          </p>
        ) : null}
        {launchAttributionLoading ? (
          <p className="ops-health-muted">
            Loading Camden launch attribution...
          </p>
        ) : null}
        {launchAttributionError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            <strong>Unable to load launch attribution.</strong>
            <p>Refresh this report or check admin access if the issue persists.</p>
          </div>
        ) : null}
        {!launchAttributionLoading &&
        !launchAttributionError &&
        launchAttribution &&
        launchTotalEvents === 0 &&
        launchVisitTotalEvents === 0 &&
        launchAuthTotalEvents === 0 &&
        launchMagicLinkPlayerActionTotalEvents === 0 &&
        launchShareFunnelTotalEvents === 0 ? (
          <div className="ops-health-empty">
            <p>No durable launch attribution events yet.</p>
            <p>
              New visits, CTA clicks, posts, accepts, reservations,
              magic-link auth, share funnel, settlements, and Action Credit
              events will appear here after they are recorded.
            </p>
          </div>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchVisitTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-visit-summary"
            >
              {VISIT_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={VISIT_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.visitTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'visitTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            {launchVisitSourceRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for visit funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {VISIT_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {VISIT_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {launchVisitSourceRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'visitSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {VISIT_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'visitSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchAuthTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-auth-summary"
            >
              {AUTH_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={AUTH_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.authTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'authTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            {launchAuthSourceRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for magic-link auth funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {AUTH_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {AUTH_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {launchAuthSourceRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'authSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {AUTH_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'authSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchShareFunnelTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-share-funnel-summary"
            >
              {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={SHARE_FUNNEL_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.shareFunnelTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'shareFunnelTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            <p className="ops-health-muted ops-health-proof-copy">
              Share funnel tracks public task-share visitors from share landing
              through backend-confirmed accept, verified completion, and Action
              Credit when the same share-acquired helper closes the loop. It is
              separate from canonical task-source attribution.
            </p>
            {shareFunnelRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for share funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source / Share</th>
                      {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {SHARE_FUNNEL_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Landing -&gt; Accepted</th>
                      <th scope="col">Accepted -&gt; Completed</th>
                      <th scope="col">Landing -&gt; Completed</th>
                      <th scope="col">Funnel events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareFunnelRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'shareFunnelSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.displayLabel || row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'shareFunnelSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatConversionWithFraction(
                            row.conversion?.landingToAcceptSuccessRate,
                            row.counts.localops_share_task_accept_succeeded,
                            row.counts.localops_share_landing_viewed,
                          )}
                        </td>
                        <td>
                          {formatConversionWithFraction(
                            row.conversion
                              ?.acceptSuccessToCompletionVerifiedRate,
                            row.counts.localops_task_completion_verified,
                            row.counts.localops_share_task_accept_succeeded,
                          )}
                        </td>
                        <td>
                          {formatConversionWithFraction(
                            row.conversion?.landingToCompletionVerifiedRate,
                            row.counts.localops_task_completion_verified,
                            row.counts.localops_share_landing_viewed,
                          )}
                        </td>
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchMagicLinkPlayerActionTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-player-action-summary"
            >
              {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={LAUNCH_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.magicLinkPlayerActionTotalsByEvent?.[
                      eventName
                    ],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'magicLinkPlayerActionTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
              <Metric
                label="Matched player actions"
                value={formatCount(
                  launchAttribution.magicLinkPlayerActionCoverage?.attributed,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'magicLinkPlayerActionCoverage:attributed',
                )}
              />
              <Metric
                label="Player-action coverage"
                value={formatActionCreditCoverage({
                  total: launchAttribution.magicLinkPlayerActionCoverage?.total,
                  attributed:
                    launchAttribution.magicLinkPlayerActionCoverage?.attributed,
                  coverage:
                    launchAttribution.magicLinkPlayerActionCoverage?.coverage,
                })}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'magicLinkPlayerActionCoverage:total',
                )}
              />
            </div>
            {magicLinkPlayerActionRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for magic-link player actions</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {LAUNCH_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {magicLinkPlayerActionRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'magicLinkPlayerActionSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'magicLinkPlayerActionSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError && launchAttribution && launchTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-canonical-summary"
            >
              {LAUNCH_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={LAUNCH_EVENT_LABELS[eventName]}
                  value={formatCount(launchAttribution.totalsByEvent?.[eventName])}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'totalsByEvent',
                    eventName,
                  )}
                />
              ))}
              <Metric
                label="Unattributed canonical events"
                value={formatCount(
                  launchAttribution.unattributedCanonicalEventCount,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'unattributedCanonicalEventCount',
                )}
              />
              <Metric
                label="Source to Action Credit"
                value={formatActionCreditCoverage(
                  launchAttribution.actionCreditJoinCoverage,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'actionCreditJoinCoverage:attributed',
                )}
              />
            </div>
            <div className="ops-health-table-wrap">
              <table className="ops-health-source-table">
                <caption>Source breakdown for canonical events</caption>
                <thead>
                  <tr>
                    <th scope="col">Source</th>
                    {LAUNCH_EVENT_ORDER.map((eventName) => (
                      <th key={eventName} scope="col">
                        {LAUNCH_EVENT_LABELS[eventName]}
                      </th>
                    ))}
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {launchSourceRows.map((row) => {
                    const rowDelta = getSourceRowDelta(
                      launchDeltaLookup,
                      'eventBreakdown',
                      row.sourceLabel,
                    );
                    return (
                    <tr
                      key={row.sourceLabel}
                      className={
                        rowDelta ? 'ops-health-source-row--changed' : ''
                      }
                    >
                      <th scope="row">
                        <span>{row.sourceLabel}</span>
                        {row.details ? <small>{row.details}</small> : null}
                        <DeltaBadge delta={rowDelta} />
                      </th>
                      {LAUNCH_EVENT_ORDER.map((eventName) => {
                        const cellDelta = getSourceCellDelta(
                          launchDeltaLookup,
                          'eventBreakdown',
                          row.sourceLabel,
                          eventName,
                        );
                        return (
                        <td
                          key={eventName}
                          className={
                            cellDelta ? 'ops-health-source-cell--changed' : ''
                          }
                        >
                          {formatCount(row.counts[eventName])}
                          <DeltaBadge delta={cellDelta} />
                        </td>
                        );
                      })}
                      <td>
                        {formatCount(row.total)}
                        <DeltaBadge delta={rowDelta} />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>

      <section
        className="ops-health-panel ops-health-operator"
        data-testid="ops-health-operator-visibility"
      >
        <div className="ops-health-section-heading">
          <h2>LocalOps Operator Visibility</h2>
          <span>
            {operatorVisibility?.proofType || 'read-only cockpit'}
          </span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Read-only Camden operating view. Metrics marked not tracked yet are
          unavailable in current instrumentation and are not treated as zero.
        </p>
        <div
          className="ops-health-launch-pulse ops-health-operator-pulse"
          data-testid="ops-health-operator-pulse"
        >
          <span
            className={`ops-health-live-pill ops-health-live-pill--${launchRealtimeStatus}`}
          >
            {launchRealtimeStatus === 'live' ? 'Live' : 'Manual refresh'}
          </span>
          <span>
            Last updated {formatDate(operatorVisibilityRefreshedAt)}
          </span>
          <small>
            Refreshes with Ops Health invalidation; deltas compare to the last
            snapshot.
          </small>
        </div>
        {operatorVisibility?.generatedAt ? (
          <p className="ops-health-mini-meta">
            Generated {formatDate(operatorVisibility.generatedAt)}
            {operatorVisibility?.filters?.marketSlug
              ? ` / market ${operatorVisibility.filters.marketSlug}`
              : ''}
          </p>
        ) : null}
        {operatorVisibilityLoading ? (
          <p className="ops-health-muted">Loading operator visibility...</p>
        ) : null}
        {operatorVisibilityError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {operatorVisibilityError}
          </div>
        ) : null}
        {operatorVisibility ? (
          <>
            <div
              className="ops-health-list ops-health-operator-summary"
              data-testid="ops-health-operator-summary"
            >
              <Metric
                label="Local players"
                value={operatorSummary.totalLocalPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.totalLocalPlayers')}
              />
              <Metric
                label="New players 7d"
                value={operatorSummary.newLocalPlayers7d}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.newLocalPlayers7d')}
              />
              <Metric
                label="Reachable"
                value={operatorSummary.reachablePlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.reachablePlayers')}
              />
              <Metric
                label="Map ready"
                value={operatorSummary.mapReadyPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.mapReadyPlayers')}
              />
              <Metric
                label="Task eligible"
                value={operatorSummary.taskEligiblePlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.taskEligiblePlayers')}
              />
              <Metric
                label="Verified Local"
                value={operatorSummary.verifiedLocalPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.verifiedLocalPlayers')}
              />
              <Metric
                label="Open funded tasks"
                value={operatorSummary.openFundedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.openFundedTasks')}
              />
              <Metric
                label="Accepted tasks"
                value={operatorSummary.acceptedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.acceptedTasks')}
              />
              <Metric
                label="Completed tasks"
                value={operatorSummary.completedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.completedTasks')}
              />
              <Metric
                label="Available offerings"
                value={operatorSummary.availableOfferings}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.availableOfferings')}
              />
              <Metric
                label="Action Credit issued"
                value={operatorSummary.actionCreditIssued}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.actionCreditIssued')}
              />
              <Metric
                label="Source rows"
                value={operatorSummary.sourceActivityRows}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.sourceActivityRows')}
              />
            </div>

            <div
              className="ops-health-triage"
              data-testid="ops-health-operator-triage"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Operator Triage</h3>
                <span>{operatorActionability.triageCards.length} signals</span>
              </div>
              <p className="ops-health-muted">
                Read-only labels derived from existing Ops Health payloads.
              </p>
              <div className="ops-health-triage-grid">
                {operatorActionability.triageCards.map((card) => (
                  <TriageCard key={card.title} card={card} />
                ))}
              </div>
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-activation-funnel"
              >
                <h3>Activation Funnel</h3>
                <div className="ops-health-operator-fields">
                  <OperatorField
                    label="Magic requested"
                    value={operatorActivationFunnel.magicLinksRequested}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.magicLinksRequested')}
                  />
                  <OperatorField
                    label="Magic completed"
                    value={operatorActivationFunnel.magicLinksCompleted}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.magicLinksCompleted')}
                  />
                  <OperatorField
                    label="PlayerMap arrivals"
                    value={operatorActivationFunnel.playerMapArrivals}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.playerMapArrivals')}
                  />
                  <OperatorField
                    label="Location saved"
                    value={operatorActivationFunnel.locationSaved}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.locationSaved')}
                  />
                  <OperatorField
                    label="First task created"
                    value={operatorActivationFunnel.firstTaskCreated}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.firstTaskCreated')}
                  />
                  <OperatorField
                    label="First offering created"
                    value={operatorActivationFunnel.firstOfferingCreated}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.firstOfferingCreated')}
                  />
                </div>
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-location-readiness"
              >
                <h3>Location / Map Readiness</h3>
                <div className="ops-health-operator-fields">
                  <OperatorField
                    label="Saved"
                    value={operatorLocationReadiness.saved}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.saved')}
                  />
                  <OperatorField
                    label="Missing"
                    value={operatorLocationReadiness.missing}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.missing')}
                  />
                  <OperatorField
                    label="Stale"
                    value={operatorLocationReadiness.stale}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.stale')}
                  />
                  <OperatorField
                    label="Raw coordinates"
                    value={
                      operatorLocationReadiness.rawCoordinatesExposed
                        ? 'exposed'
                        : 'not exposed'
                    }
                  />
                </div>
              </article>
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-active-player-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Active Local Players</h3>
                <span>{operatorPlayerRows.length} rows</span>
              </div>
              {operatorActionability.playerRows.length ? (
                operatorActionability.playerRows.slice(0, 8).map((row) => {
                  const playerDeltaId = getOpsSnapshotRowKey(row.playerId);
                  return (
                  <article className="ops-health-operator-row" key={row.playerId}>
                    <div>
                      <strong>{getPlayerDisplayLabel(row)}</strong>
                      <EntityDebugId label="Player ID" value={row.playerId} />
                      <PlayerUsefulnessNote usefulness={row.usefulness} />
                    </div>
                    <OperatorField label="Role" value={row.role || '--'} />
                    <OperatorField label="Location" value={row.locationStatus || '--'} />
                    <OperatorField
                      label="AC"
                      value={row.actionCreditCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:actionCreditCount`,
                      )}
                    />
                    <OperatorField
                      label="Magic completed"
                      value={row.magicLink?.loginCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:magicLink.loginCount`,
                      )}
                    />
                    <OperatorField
                      label="Activation sends"
                      value={row.activationEmail?.sendCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:activationEmail.sendCount`,
                      )}
                    />
                    <OperatorField label="Last active" value={formatDate(row.lastActiveAt)} />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No active LocalOps players in this window.</p>
              )}
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-task-visibility-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Task Visibility</h3>
                <span>{operatorTaskRows.length} rows</span>
              </div>
              {operatorActionability.taskRows.length ? (
                operatorActionability.taskRows.slice(0, 8).map((row) => {
                  const taskDeltaId = getOpsSnapshotRowKey(row.taskId);
                  return (
                  <article className="ops-health-operator-row" key={row.taskId}>
                    <div>
                      <strong>{getTaskDisplayTitle(row)}</strong>
                      <EntityDebugId label="Task ID" value={row.taskId} />
                      <ActionabilityNote actionability={row.actionability} />
                    </div>
                    <OperatorField label="Status" value={row.status || '--'} />
                    <OperatorField label="Creator" value={row.creatorPlayerId || '--'} />
                    <OperatorField label="Helper" value={row.acceptedHelperPlayerId || 'unassigned'} />
                    <OperatorField
                      label="Reopens"
                      value={row.reopenCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:reopenCount`,
                      )}
                    />
                    <OperatorField
                      label="Releases"
                      value={row.helperReleaseCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:helperReleaseCount`,
                      )}
                    />
                    <OperatorField
                      label="Fanout email sent"
                      value={row.fanout?.emailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.emailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Fanout email opened"
                      value={row.fanout?.emailPixelOpens}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.emailPixelOpens`,
                      )}
                    />
                    <OperatorField
                      label="Fanout last open"
                      value={
                        row.fanout?.lastEmailOpenedAt
                          ? formatDate(row.fanout.lastEmailOpenedAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Owner re-notify"
                      value={row.fanout?.ownerReNotifyCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.ownerReNotifyCount`,
                      )}
                    />
                    <OperatorField
                      label="Re-notify status"
                      value={row.fanout?.ownerReNotifyStatus || '--'}
                    />
                    <OperatorField
                      label="Last re-notify"
                      value={
                        row.fanout?.lastOwnerReNotifyAt
                          ? formatDate(row.fanout.lastOwnerReNotifyAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Re-notify recipients"
                      value={row.fanout?.lastOwnerReNotifyRecipientCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.lastOwnerReNotifyRecipientCount`,
                      )}
                    />
                    <OperatorField
                      label="Re-notify email sent"
                      value={row.fanout?.lastOwnerReNotifyEmailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.lastOwnerReNotifyEmailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Raise-pay status"
                      value={row.raisePay?.renotifyStatus || '--'}
                    />
                    <OperatorField
                      label="Last pay raise"
                      value={
                        row.raisePay?.lastPayRaiseAt
                          ? formatDate(row.raisePay.lastPayRaiseAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Raise-pay recipients"
                      value={row.raisePay?.renotifyRecipientCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:raisePay.renotifyRecipientCount`,
                      )}
                    />
                    <OperatorField
                      label="Raise-pay email sent"
                      value={row.raisePay?.renotifyEmailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:raisePay.renotifyEmailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Next re-notify"
                      value={
                        row.fanout?.nextEligibleOwnerReNotifyAt
                          ? formatDate(row.fanout.nextEligibleOwnerReNotifyAt)
                          : '--'
                      }
                    />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No task rows in this window.</p>
              )}
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-offering-visibility-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Offering Visibility</h3>
                <span>{operatorOfferingRows.length} rows</span>
              </div>
              {operatorOfferingRows.length ? (
                operatorOfferingRows.slice(0, 8).map((row) => {
                  const offeringDeltaId = getOpsSnapshotRowKey(row.offeringId);
                  return (
                  <article className="ops-health-operator-row" key={row.offeringId}>
                    <div>
                      <strong>{getOfferingDisplayTitle(row)}</strong>
                      <EntityDebugId label="Offering ID" value={row.offeringId} />
                    </div>
                    <OperatorField label="Status" value={row.status || '--'} />
                    <OperatorField label="Owner" value={row.ownerPlayerId || '--'} />
                    <OperatorField label="Price" value={row.price === null ? '--' : formatUsd(row.price)} />
                    <OperatorField
                      label="Reservations"
                      value={row.reservations}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:reservations`,
                      )}
                    />
                    <OperatorField
                      label="Fulfilled"
                      value={row.fulfilledOrders}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:fulfilledOrders`,
                      )}
                    />
                    <OperatorField
                      label="Share landings"
                      value={row.shareLandings}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:shareLandings`,
                      )}
                    />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No offering rows in this window.</p>
              )}
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-source-attribution"
              >
                <h3>Source Attribution</h3>
                {operatorSourceRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorSourceRows.slice(0, 6).map((row) => {
                      const rowDelta = getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:sourceAttributionRows:${row.sourceKey}:total`,
                      );
                      return (
                        <li key={row.sourceKey}>
                          <strong>{row.sourceKey}</strong>
                          <span>
                            {formatCount(row.total)} tracked events
                            <DeltaBadge delta={rowDelta} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No source rows in this window.</p>
                )}
                {operatorActionability.sourceRows.length ? (
                  <>
                    <h4 className="ops-health-operator-subhead">
                      Source Outcome Quality
                    </h4>
                    <ul className="ops-health-operator-stack">
                      {operatorActionability.sourceRows.slice(0, 6).map((row) => (
                        <li key={`outcome-${row.key}`}>
                          <strong>{row.displayLabel}</strong>
                          <span>{row.evidence}</span>
                          <em>{row.outcomeLabel}</em>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-email-proof"
              >
                <h3>Email & Notification Proof</h3>
                <p className="ops-health-muted">
                  Pixel opens are approximate image-load telemetry, not read confirmation.
                </p>
                {operatorEmailRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorEmailRows.slice(0, 6).map((row) => {
                      const entityTitle = getOperatorEmailEntityTitle(row);
                      const entityType =
                        row.entityType || (row.taskId ? 'task' : 'entity');
                      const entityId =
                        row.entityId || row.taskId || row.offeringId || '';
                      const rowKey = row.proofId || row.notificationId;
                      const proofDeltaId = getOpsSnapshotRowKey(rowKey);
                      const pixelOpenDelta = getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:emailNotificationProofRows:${proofDeltaId}:pixelOpenCount`,
                      );
                      return (
                        <li key={rowKey}>
                          <strong>{row.emailType || row.proofType}</strong>
                          {entityTitle ? <span>{entityTitle}</span> : null}
                          {entityId ? (
                            <span className="ops-health-entity-meta">
                              {entityType} ID {formatShortId(entityId)}
                            </span>
                          ) : null}
                          <span>
                            {row.sendStatus || '--'} / pixel opens{' '}
                            {getMetricValue(row.pixelOpenCount)}
                            <DeltaBadge delta={pixelOpenDelta} />
                          </span>
                          {row.firstPixelOpenedAt ? (
                            <span>first open {formatDate(row.firstPixelOpenedAt)}</span>
                          ) : null}
                          {row.lastPixelOpenedAt ? (
                            <span>last open {formatDate(row.lastPixelOpenedAt)}</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No email proof rows in this window.</p>
                )}
              </article>
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-action-credit"
              >
                <h3>Action Credit / Verified Completion</h3>
                {operatorActionability.actionCreditRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorActionability.actionCreditRows.slice(0, 6).map((row) => (
                      <li key={row.actionCreditId}>
                        <strong>{row.title}</strong>
                        <span>{row.meta}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No Action Credit rows in this window.</p>
                )}
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-loc-readiness"
              >
                <h3>LOC Readiness</h3>
                <div className="ops-health-operator-fields">
                  <OperatorField label="Status" value={operatorLocPlaceholder.status || 'not enabled yet'} />
                  <OperatorField label="Future page" value={operatorLocPlaceholder.futurePage || 'planned'} />
                  <OperatorField label="Future role" value={operatorLocPlaceholder.futureRole || 'planned'} />
                  <OperatorField label="Captured leads" value={operatorLocPlaceholder.capturedLeads || 'not enabled'} />
                </div>
              </article>
            </div>
          </>
        ) : null}
      </section>

      <section
        className="ops-health-panel ops-health-settlement-queue"
        data-testid="ops-health-settlement-queue"
      >
        <div className="ops-health-section-heading">
          <h2>Offering Settlement Queue</h2>
          <span>{settlementRows.length} rows</span>
        </div>
        {settlementError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {settlementError}
          </div>
        ) : null}
        {settlementNotice ? (
          <div className="ops-health-alert ops-health-alert--success">
            {settlementNotice}
          </div>
        ) : null}
        {settlementLoading ? (
          <p className="ops-health-muted">Loading settlement queue...</p>
        ) : null}
        {!settlementLoading && settlementRows.length === 0 ? (
          <p className="ops-health-muted">
            No stuck offering settlements are waiting for admin action.
          </p>
        ) : null}
        {settlementRows.length > 0 ? (
          <div className="ops-health-settlement-results">
            {settlementRows.map((row) => {
              const rowKey = row.orderId || `${row.offeringTitle}-${row.updatedAt}`;
              const allowedActions = Array.isArray(row.allowedActions)
                ? row.allowedActions
                : [];
              const refundBusy =
                settlementActionBusy === `${row.orderId}:refund_buyer`;
              const payBusy =
                settlementActionBusy === `${row.orderId}:pay_seller`;
              return (
                <article
                  className="ops-health-settlement-row"
                  key={rowKey}
                  data-testid="ops-health-settlement-row"
                >
                  <div className="ops-health-task-row-main">
                    <strong>{row.offeringTitle || 'Untitled offering'}</strong>
                    <span>{row.orderId}</span>
                  </div>
                  <div className="ops-health-task-row-meta">
                    <span>{row.classification || 'Unclassified'}</span>
                    <span>buyer {row.buyer?.playerId || '--'}</span>
                    <span>seller {row.seller?.playerId || '--'}</span>
                    <span>qty {row.quantity ?? '--'}</span>
                    <span>{formatUsd(row.amountUsd)} / {formatJgk(row.amountJGK)}</span>
                  </div>
                  <div className="ops-health-task-row-badges">
                    <span>{row.status || 'unknown'} order</span>
                    <span>{row.escrowStatus || 'none'} escrow</span>
                    <span>{row.settlementStatus || 'none'} settlement</span>
                    <span>{row.settlementOutcome || 'none'} outcome</span>
                    <span>confirmed {formatDate(row.providerConfirmedAt)}</span>
                    <span>confirm by {formatDate(row.confirmByAt)}</span>
                    <span>updated {formatDate(row.updatedAt)}</span>
                  </div>
                  <div className="ops-health-settlement-actions">
                    {allowedActions.includes('refund_buyer') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'refund_buyer')}
                        disabled={!!settlementActionBusy}
                      >
                        {refundBusy ? 'Refunding...' : 'Refund buyer'}
                      </button>
                    ) : null}
                    {allowedActions.includes('pay_seller') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'pay_seller')}
                        disabled={!!settlementActionBusy}
                      >
                        {payBusy ? 'Paying...' : 'Pay seller'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        window.navigator?.clipboard?.writeText?.(row.orderId)
                      }
                    >
                      Copy order ID
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="ops-health-grid">
        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Readiness</h2>
            <span>{truthyLabel(readinessSummary.pass)}</span>
          </div>
          <div className="ops-health-list">
            <Metric label="Env health" value={truthyLabel(staticConfig.pass)} />
            <Metric
              label="Escrow player"
              value={truthyLabel(systemPlayers.escrow?.exists)}
            />
            <Metric
              label="Treasury player"
              value={truthyLabel(systemPlayers.treasury?.exists)}
            />
            <Metric
              label="Stripe prices"
              value={truthyLabel(stripe.allPriceEnvsPresent)}
            />
            <Metric
              label="Stripe webhook"
              value={truthyLabel(stripe.webhookSecret?.present)}
            />
            <Metric
              label="Secrets redacted"
              value={truthyLabel(readinessSummary.secretsRedacted)}
            />
          </div>
          <div className="ops-health-flags">
            <span>Ignition</span>
            <strong>
              incentives {String(ignitionFlags.incentives?.effective ?? 'unknown')}
            </strong>
            <strong>
              rewards {String(ignitionFlags.rewards?.effective ?? 'unknown')}
            </strong>
            <strong>
              boost {String(ignitionFlags.boost?.effective ?? 'unknown')}
            </strong>
          </div>
        </div>

        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Accounting Rollup</h2>
            <span>{rollup?.proofType || 'not loaded'}</span>
          </div>
          <div className="ops-health-list">
            <Metric
              label="Platform revenue"
              value={formatJgk(rollup?.platformRevenueJGK)}
            />
            <Metric
              label="Contra revenue / boosts"
              value={formatJgk(rollup?.contraRevenueJGK)}
            />
            <Metric
              label="Promo issuance / rewards"
              value={formatJgk(rollup?.promoIssuanceJGK)}
            />
            <Metric
              label="Helper payouts"
              value={formatJgk(rollup?.helperPayoutsJGK)}
            />
            <Metric label="Escrow held" value={formatJgk(rollup?.escrowHeldJGK)} />
            <Metric
              label="Escrow released"
              value={formatJgk(rollup?.escrowReleasedJGK)}
            />
            <Metric
              label="Cashout held"
              value={formatJgk(rollup?.cashoutHeldJGK)}
            />
            <Metric
              label="Cashout paid"
              value={formatJgk(rollup?.cashoutSettledJGK)}
            />
            <Metric
              label="Cashout released"
              value={formatJgk(rollup?.cashoutReleasedJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(rollup?.netPlatformPositionJGK)}
            />
          </div>
        </div>
      </section>

      <section className="ops-health-panel ops-health-task-finder">
        <div className="ops-health-section-heading">
          <h2>Find Recent Tasks</h2>
          <span>{taskResults.length} results</span>
        </div>
        <form
          className="ops-health-finder-form"
          data-testid="ops-health-task-finder-form"
          onSubmit={handleTaskSearch}
        >
          <label htmlFor="ops-health-player-id">Player</label>
          <input
            id="ops-health-player-id"
            value={taskFilters.playerId}
            onChange={(event) => updateTaskFilter('playerId', event.target.value)}
            placeholder="creator or helper playerId"
          />

          <label htmlFor="ops-health-task-title">Title</label>
          <input
            id="ops-health-task-title"
            value={taskFilters.q}
            onChange={(event) => updateTaskFilter('q', event.target.value)}
            placeholder="title fragment"
          />

          <label htmlFor="ops-health-task-from">From</label>
          <input
            id="ops-health-task-from"
            type="date"
            value={taskFilters.from}
            onChange={(event) => updateTaskFilter('from', event.target.value)}
          />

          <label htmlFor="ops-health-task-to">To</label>
          <input
            id="ops-health-task-to"
            type="date"
            value={taskFilters.to}
            onChange={(event) => updateTaskFilter('to', event.target.value)}
          />

          <label htmlFor="ops-health-task-status">Status</label>
          <select
            id="ops-health-task-status"
            value={taskFilters.status}
            onChange={(event) => updateTaskFilter('status', event.target.value)}
          >
            {TASK_STATUS_OPTIONS.map((statusOption) => (
              <option key={statusOption || 'all'} value={statusOption}>
                {statusOption || 'Any status'}
              </option>
            ))}
          </select>

          <label htmlFor="ops-health-task-limit">Limit</label>
          <input
            id="ops-health-task-limit"
            type="number"
            min="1"
            max="100"
            value={taskFilters.limit}
            onChange={(event) => updateTaskFilter('limit', event.target.value)}
          />

          <label className="ops-health-toggle" htmlFor="ops-health-boosted">
            <input
              id="ops-health-boosted"
              type="checkbox"
              checked={taskFilters.boosted}
              onChange={(event) =>
                updateTaskFilter('boosted', event.target.checked)
              }
            />
            Boosted only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-rewarded">
            <input
              id="ops-health-rewarded"
              type="checkbox"
              checked={taskFilters.rewarded}
              onChange={(event) =>
                updateTaskFilter('rewarded', event.target.checked)
              }
            />
            Rewarded only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-anomaly-only">
            <input
              id="ops-health-anomaly-only"
              type="checkbox"
              checked={taskFilters.anomalyOnly}
              onChange={(event) =>
                updateTaskFilter('anomalyOnly', event.target.checked)
              }
            />
            Anomaly only
          </label>

          <div className="ops-health-finder-actions">
            <button type="submit" disabled={taskSearchLoading}>
              {taskSearchLoading ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => fetchTaskResults(taskFilters)}
              disabled={taskSearchLoading}
            >
              Refresh
            </button>
          </div>
        </form>
        {taskSearchError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskSearchError}
          </div>
        ) : null}
        <div
          className="ops-health-task-results"
          data-testid="ops-health-task-results"
        >
          {taskResults.length > 0 ? (
            taskResults.map((task) => (
              <article
                className="ops-health-task-row"
                key={task.taskId}
                data-testid="ops-health-task-result"
              >
                <div className="ops-health-task-row-main">
                  <strong>{task.title || 'Untitled task'}</strong>
                  <span>{task.taskId}</span>
                </div>
                <div className="ops-health-task-row-meta">
                  <span>{task.status || 'unknown'}</span>
                  <span>creator {task.creatorPlayerId || '--'}</span>
                  <span>helper {task.helperPlayerId || 'unassigned'}</span>
                  <span>updated {formatDate(task.updatedAt)}</span>
                </div>
                <div className="ops-health-task-row-badges">
                  <span>{formatJgk(task.priceJGK)} escrow</span>
                  <span>{task.escrowStatus || 'none'} escrow</span>
                  <span>{task.payoutStatus || 'unpaid'} payout</span>
                  {task.hasBoost ? (
                    <span>{formatJgk(task.platformBoostJGK)} boost</span>
                  ) : null}
                  {task.hasRewards ? (
                    <span>{formatJgk(task.creatorRewardsJGK)} rewards</span>
                  ) : null}
                  <span>{task.anomalyCount || 0} anomalies</span>
                </div>
                <button
                  type="button"
                  onClick={() => loadTaskReport(task.taskId)}
                  disabled={taskLoading}
                >
                  Open accounting report
                </button>
              </article>
            ))
          ) : (
            <p className="ops-health-muted">
              No recent tasks match the current filters.
            </p>
          )}
        </div>
      </section>

      <section className="ops-health-panel">
        <div className="ops-health-section-heading">
          <h2>Advanced: Lookup By Task Id</h2>
          <span>task-level proof</span>
        </div>
        <form className="ops-health-task-form" onSubmit={handleTaskLookup}>
          <label htmlFor="ops-health-task-id">Task id</label>
          <input
            id="ops-health-task-id"
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            placeholder="Paste task ObjectId"
          />
          <button type="submit" disabled={taskLoading}>
            {taskLoading ? 'Checking...' : 'Lookup'}
          </button>
        </form>
        {taskError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskError}
          </div>
        ) : null}
        {taskReport ? (
          <div className="ops-health-task-report" data-testid="ops-health-task-report">
            <Metric label="Task id" value={taskReport.taskId || '--'} />
            <Metric label="Escrow" value={formatJgk(taskReport.escrowAmountJGK)} />
            <Metric
              label="Payout"
              value={formatJgk(
                taskReport.expectedHelperPayoutJGK ??
                  taskReport.summary?.helperPayoutJGK,
              )}
            />
            <Metric label="Fee" value={formatJgk(taskReport.netPlatformFeeJGK)} />
            <Metric label="Boost" value={formatJgk(taskReport.platformBoostJGK)} />
            <Metric
              label="Rewards"
              value={formatJgk(taskReport.summary?.creatorRewardsJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(taskReport.summary?.netPlatformPositionJGK)}
            />
            <Metric
              label="Task anomalies"
              value={(taskReport.anomalies || []).length}
            />
            <JsonDetails
              title="Raw task JSON"
              payload={taskReport}
              testId="ops-health-task-json"
            />
          </div>
        ) : null}
      </section>

      <JsonDetails
        title="Raw readiness and rollup JSON"
        payload={{ readiness, rollup }}
        testId="ops-health-raw-json"
      />
    </main>
  );
}
